import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useAppTranslation } from '../../i18n/useAppTranslation';
import styles from "./Navigation.module.css";
import { formatDateDdMmmYyyy, formatDateUS } from '../../utils/dateFormat';
import { formatPercent, formatFixed, formatDelta } from '../../utils/numberFormat';
import { getProjectOverride } from "../../Data/ProjectConfig/projectConfigOverrides";
import { getProjectKpiConfigOverride, upsertProjectKpiConfigOverride } from "../../Data/ProjectConfig/projectKpiConfigOverrides";
import {
  getFinanceCostSeriesForInstance,
  setFinanceCostSeriesForInstance,
  getBudgetCurveMetaForInstance,
  setBudgetCurveMetaForInstance,
  type FinanceCurveType,
  type FinanceCostPoint,
  type ReportingCadence,
  type CostDirection,
  type ReportingSchedule,
} from "../../Data/Budget/budgetOverrides";
import { buildReportingDates as buildReportingDatesShared } from "../../kpi-engine/reportingDates";
import { getAllProjectsSeedPlusAdded } from "../../Data/ProjectConfig";
import {
  canAdminProject,
  canEditProjectContent,
} from "../../security/securityAdapter";
import { isFinanceHydrated } from "../../Data/store";
import { useApiProjectRoles } from "../../api/useApiProjectRoles";
import { getCurrency, getCurrencySymbol, formatCurrency, formatCurrencyCompact } from "../../Data/Budget/currencyLookup";

import { kpiConfig } from "../../Data/KpiEngine/kpiConfig";

import ExcelPasteGrid from "../ExcelPasteGrid/ExcelPasteGrid";
import {
  batchUpsertBudgetSeriesPoints,
  deleteBudgetSeriesPointsByDateRange,
  type CreateBudgetSeriesPointRequest,
} from "../../api/budgetSeriesPoints";

import {
  isoToUS,
  isoToDdMmmYyyy,
  usToISO,
  isISODate,
  toISODate,
  parseISODateToMs,
  clamp01,
  clamp,
  normalizeToleranceToFraction,
  fmtPct,
  fmtAsOfDate,
  readVarianceTolerancesFromInstance,
  buildPath,
  buildBandPath,
  InfoIcon,
  interpolateBaselineAtDate,
  useDateFilteredSeries,
  useForecastBandWidths,
  ThermometerTooltip,
  TooltipRowConfig,
  ThermometerLegend,
  useStatusLog,
  PipelineStatusLog,
  StatusEntry,
  VarianceSegment,
  buildVarianceSegments,
} from "./thermometerUtils";

type Props = {
  projectGUID?: string;
  currentUserGuid?: string;
  instanceGuid?: string;
  title?: string;
  displayMode?: "full" | "mini";

  // Optional debug plumbing (Finance tab debug window)
  // index.tsx may pass these; keep them optional to avoid touching call sites.
  debugMode?: boolean;
  onDebugUpdate?: (d: any) => void;

  /** Pre-fetched series data from adapter. When provided, skips internal localStorage read for chart rendering. */
  prefetchedSeries?: FinanceCostPoint[];
  /** Pre-fetched curve metadata from adapter. When provided, skips internal localStorage read for chart rendering. */
  prefetchedCurveMeta?: any;
  /** API project record. When provided, used for project dates instead of seed-only lookup. */
  apiProjectRecord?: any;
  /** When true, renders without the outer scheduleChartWrap border/background (parent provides it). */
  embedded?: boolean;
  /** Gantt tasks for WBS picker (Professional tier). When provided, the manage modal shows a WBS scope dropdown. */
  ganttTasks?: import('../../api/ganttTasks').GanttTask[];
};

function inferScaleFactor(rows: FinanceCostPoint[]) {
  const usable = (rows || []).filter((r) =>
    [r.baseline_budget, r.actual_spend, r.forecast_spend].some((v) =>
      Number.isFinite(Number(v)),
    ),
  );
  let maxV = 0;
  for (const r of usable) {
    maxV = Math.max(
      maxV,
      Number.isFinite(r.baseline_budget as any) ? Number(r.baseline_budget) : 0,
      Number.isFinite(r.actual_spend as any) ? Number(r.actual_spend) : 0,
      Number.isFinite(r.forecast_spend as any) ? Number(r.forecast_spend) : 0,
    );
  }
  return maxV > 0 ? maxV : 1;
}

/** Collapsible section wrapper for the modal redesign (DEV-787). */
function CollapsibleSection({
  title,
  defaultOpen,
  children,
}: {
  title: string;
  defaultOpen: boolean;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{ borderBottom: "1px solid var(--border-subtle)" }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          width: "100%",
          padding: "10px 0",
          background: "none",
          border: "none",
          color: "var(--text-primary)",
          cursor: "pointer",
          fontSize: 13,
          fontWeight: 600,
          letterSpacing: 0.15,
        }}
      >
        <span>{title}</span>
        <span
          style={{
            fontSize: 10,
            transition: "transform 0.15s",
            transform: open ? "rotate(180deg)" : "rotate(0deg)",
            opacity: 0.55,
          }}
        >
          ▼
        </span>
      </button>
      {open ? (
        <div style={{ paddingBottom: 12 }}>{children}</div>
      ) : null}
    </div>
  );
}

export default function BudgetThermometer({
  projectGUID,
  currentUserGuid,
  instanceGuid,
  title,
  displayMode,
  debugMode,
  prefetchedSeries,
  prefetchedCurveMeta,
  apiProjectRecord,
  embedded,
  ganttTasks,
}: Props) {
  const { t } = useAppTranslation('kpi');
  const [dataVersion, setDataVersion] = useState(0);
  const [fullOpen, setFullOpen] = useState(false);

  // ── Pipeline status log — visible under the chart after Apply ──
  const { statusLog, setStatusLog, statusLogRef, pushStatus } = useStatusLog();

  useEffect(() => {
    const onChange = () => setDataVersion((v) => v + 1);
    window.addEventListener("l4e_finance_cost_changed" as any, onChange);
    return () =>
      window.removeEventListener("l4e_finance_cost_changed" as any, onChange);
  }, []);

  // ---- Baseline curve management (stored locally per project) ----
  // Prefer API project record (has DB dates); fall back to seed/localStorage.
  const projectRecord = useMemo(() => {
    if (apiProjectRecord) return apiProjectRecord;
    if (!projectGUID) return null;
    const all = getAllProjectsSeedPlusAdded() || [];
    const g = String(projectGUID).toUpperCase();
    return (
      all.find(
        (p: any) => String(p?.project_GUID || p?.project_guid || "").toUpperCase() === g,
      ) || null
    );
  }, [projectGUID, apiProjectRecord]);

  const projectGuidNorm = String(
    (projectRecord as any)?.project_GUID ||
      (projectRecord as any)?.project_guid ||
      projectGUID ||
      "",
  ).trim();

  // Project role slots for the role assignment picker
  const { slots: projectRoleSlots } = useApiProjectRoles(projectGuidNorm || null);

  // Instance-scoped storage keying (v2). The stored payload keeps project_guid + instance_guid fields.
  const instanceGuidNorm = String(instanceGuid || "").trim();
  const storageKey = projectGuidNorm;

  const projOverride = projectGuidNorm
    ? getProjectOverride(projectGuidNorm as any)
    : null;

  const startISO = String(
    (projOverride as any)?.project_start_date ||
      (projectRecord as any)?.project_start_date ||
      (projectRecord as any)?.start_date ||
      (projectRecord as any)?.startDate ||
      "",
  ).trim();

  const endISO = String(
    (projOverride as any)?.project_end_date ||
      (projectRecord as any)?.project_end_date ||
      (projectRecord as any)?.finish_date ||
      (projectRecord as any)?.end_date ||
      (projectRecord as any)?.finishDate ||
      "",
  ).trim();

  // Prefer pre-fetched data from adapter on initial render; after Apply
  // bumps dataVersion, re-read from localStorage so the chart refreshes.
  const series = useMemo(() => {
    if (prefetchedSeries && dataVersion === 0) return prefetchedSeries;
    void dataVersion;
    if (!projectGUID) return [] as FinanceCostPoint[];
    const p = projectGuidNorm || storageKey || String(projectGUID);
    const meta =
      (prefetchedCurveMeta && dataVersion === 0 ? prefetchedCurveMeta : null) ??
      (getBudgetCurveMetaForInstance(p, instanceGuidNorm || undefined) as any);
    const ov = getFinanceCostSeriesForInstance(
      p,
      instanceGuidNorm || undefined,
    );
    if (meta?.suppress_seed) return ov || [];
    if (ov && ov.length) return ov;

    return [];
  }, [
    prefetchedSeries,
    prefetchedCurveMeta,
    projectGUID,
    projectGuidNorm,
    storageKey,
    instanceGuidNorm,
    dataVersion,
  ]);

  // ── Date-range guard: filter out points outside the project window ──
  // This prevents stale DB records (from before a date-range change) from
  // appearing on the chart. Does NOT delete anything — purely a render filter.
  const dateFilteredSeries = useDateFilteredSeries(series, startISO, endISO);

  const latest = dateFilteredSeries.length ? dateFilteredSeries[dateFilteredSeries.length - 1] : null;

  const scaleFactor = useMemo(() => inferScaleFactor(dateFilteredSeries), [dateFilteredSeries]);

  // Split actual/forecast around "today" based on reporting_date.
  const todayISO = toISODate(new Date());
  const actualRows = useMemo(
    () =>
      dateFilteredSeries.filter(
        (r) =>
          r.reporting_date <= todayISO &&
          r.actual_spend !== null &&
          r.actual_spend !== undefined &&
          Number.isFinite(Number(r.actual_spend)),
      ),
    [dateFilteredSeries, todayISO],
  );
  const forecastRows = useMemo(
    () =>
      dateFilteredSeries.filter(
        (r) =>
          r.reporting_date > todayISO &&
          r.forecast_spend !== null &&
          r.forecast_spend !== undefined &&
          Number.isFinite(Number(r.forecast_spend)),
      ),
    [dateFilteredSeries, todayISO],
  );
  const baselineRows = useMemo(
    () =>
      dateFilteredSeries.filter(
        (r) =>
          r.baseline_budget !== null &&
          r.baseline_budget !== undefined &&
          Number.isFinite(Number(r.baseline_budget)),
      ),
    [dateFilteredSeries],
  );

  const actualLatest = actualRows.length
    ? actualRows[actualRows.length - 1]
    : latest;
  const [manageOpen, setManageOpen] = useState(false);
  const [showLegend, setShowLegend] = useState(false);
  // Variance coloring is always on (no toggle needed).
  const showVarianceColoring = true;

  // ── Budget view mode: percent (of total budget) vs absolute (currency) ──
  type BudgetViewMode = "percent" | "absolute";
  const budgetViewStorageKey = `l4e_budget_view_${projectGuidNorm}`;
  const [budgetViewMode, setBudgetViewMode] = useState<BudgetViewMode>(() => {
    try {
      const stored = localStorage.getItem(budgetViewStorageKey);
      if (stored === "absolute" || stored === "percent") return stored;
    } catch { /* ignore */ }
    return "percent";
  });
  const toggleBudgetViewMode = () => {
    setBudgetViewMode((prev) => {
      const next: BudgetViewMode = prev === "percent" ? "absolute" : "percent";
      try { localStorage.setItem(budgetViewStorageKey, next); } catch { /* ignore */ }
      return next;
    });
  };

  // ── Total budget + currency from project record ──
  const totalBudget = useMemo(() => {
    const raw = (projectRecord as any)?.budget_estimate ?? (projectRecord as any)?.budgetEstimate ?? "";
    const n = Number(String(raw).replace(/[^0-9.\-]/g, ""));
    return Number.isFinite(n) && n > 0 ? n : 0;
  }, [projectRecord]);

  const budgetCurrencyCode = useMemo(() => {
    return String(
      (projectRecord as any)?.budget_currency ??
      (projectRecord as any)?.budgetCurrency ??
      "USD"
    ).trim() || "USD";
  }, [projectRecord]);

  const currencySymbol = getCurrencySymbol(budgetCurrencyCode);

  /** True when we have a valid total budget and can show percentage-of-budget view. */
  const canShowPercent = totalBudget > 0;

  // ── View-mode–aware scale factor ──
  // In percent mode: if totalBudget is set, normalize values as % of total budget.
  //   The scaleFactor becomes totalBudget so that value/scaleFactor gives a 0..1 fraction.
  // In absolute mode: use inferScaleFactor (max data value) so chart fills the Y-axis.
  //   Y-axis labels show currency values instead of percentages.
  // Fallback: if percent mode is selected but totalBudget is 0, fall back to inferred scale.
  const effectiveScaleFactor = useMemo(() => {
    if (budgetViewMode === "percent" && totalBudget > 0) return totalBudget;
    return scaleFactor;
  }, [budgetViewMode, totalBudget, scaleFactor]);

  const isAbsoluteView = budgetViewMode === "absolute" || !canShowPercent;

  /**
   * Format a raw data value for display depending on view mode.
   * - percent mode: shows as "XX.X%" (of total budget or inferred scale)
   * - absolute mode: shows as "$1.5M" (compact currency)
   */
  const fmtValue = (raw: number): string => {
    if (isAbsoluteView) {
      return formatCurrencyCompact(raw, budgetCurrencyCode);
    }
    return fmtPct(raw, effectiveScaleFactor);
  };

  /* Overflow menu removed — replaced by pencil icon (DEV-787 Phase 1) */
  const [curveType, setCurveType] = useState<FinanceCurveType>("sigmoidal");
  const [copyFromGuid, setCopyFromGuid] = useState<string>("");
  const [wbsCode, setWbsCode] = useState<string>("");  // WBS scope for Professional tier
  const [assignedRoleSlotGuid, setAssignedRoleSlotGuid] = useState<string>(""); // Role responsible for updates
  const [projectConfigNonce, setProjectConfigNonce] = useState(0);

  useEffect(() => {
    const onCfg = () => setProjectConfigNonce((n) => n + 1);
    try {
      window.addEventListener("l4e_project_config_changed", onCfg as any);
      window.addEventListener("storage", onCfg as any); // fallback
    } catch {
      // ignore
    }
    return () => {
      try {
        window.removeEventListener("l4e_project_config_changed", onCfg as any);
        window.removeEventListener("storage", onCfg as any);
      } catch {
        // ignore
      }
    };
  }, []);

  // Reporting schedule (used to initialize date rows)
  const [reportingCadence, setReportingCadence] =
    useState<ReportingCadence>("weekly");
  const [repeatEvery, setRepeatEvery] = useState<number>(1);
  const [untilISO, setUntilISO] = useState<string>("");

  // Weekly recurrence (Outlook-style): which weekdays are included
  // 0=Sun..6=Sat (Date.getDay())
  const [weekdays, setWeekdays] = useState<boolean[]>(() => [
    false, // Sun
    true,  // Mon (default reporting day)
    false,
    false,
    false,
    false,
    false,
  ]);

  const [showWeekdayWarning, setShowWeekdayWarning] = useState(false);

  useEffect(() => {
    if (reportingCadence !== "weekly") {
      if (showWeekdayWarning) setShowWeekdayWarning(false);
      return;
    }
    if (weekdays.some(Boolean) && showWeekdayWarning)
      setShowWeekdayWarning(false);
  }, [reportingCadence, weekdays, showWeekdayWarning]);
  // Monthly recurrence options (used only when cadence === "monthly")
  type MonthlyRuleMode = "day" | "first" | "last";
  const [monthlyMode, setMonthlyMode] = useState<MonthlyRuleMode>("day");
  const [monthlyDayOfMonth, setMonthlyDayOfMonth] = useState<number>(1);
  const [monthlyWeekday, setMonthlyWeekday] = useState<number>(1); // 0=Sun..6=Sat
  // Optional admin-only overrides for progress curve window (phase inside project)
  const [overrideStartISO, setOverrideStartISO] = useState<string>("");
  const [overrideEndISO, setOverrideEndISO] = useState<string>("");

  // Inline alert/confirm system (replaces window.alert / window.confirm)
  const [modalAlert, setModalAlert] = useState<{
    message: string;
    type: "alert" | "confirm";
    onConfirm?: () => void;
  } | null>(null);

  const showAlert = (message: string) =>
    setModalAlert({ message, type: "alert" });
  const showConfirm = (message: string, onConfirm: () => void) =>
    setModalAlert({ message, type: "confirm", onConfirm });

  // Keep displayed project dates in sync with Project Settings changes.
  // Only auto-default the override window if the user hasn't explicitly changed it this session.
  const overrideTouchedRef = useRef(false);

  useEffect(() => {
    if (!manageOpen) return;
    // Sync override window with current project dates.
    // If the stored override matches the old project dates (user never customized),
    // update to the new project dates. If no override is set, default to project dates.
    setOverrideStartISO((prev) => {
      if (!prev) return startISO;
      return startISO || prev;
    });
    setOverrideEndISO((prev) => {
      if (!prev) return endISO;
      return endISO || prev;
    });
  }, [manageOpen, startISO, endISO]);

  // Default to "increasing" (0→100) for baseline generation.
  // The cost direction (over budget = red) is handled by the scoring engine,
  // not by the curve generation direction.
  const [direction, setDirection] = useState<CostDirection>("increasing");

  const canEditCurve = useMemo(() => {
    if (!projectGUID) return false;
    if (!currentUserGuid) return false;
    return canEditProjectContent(String(currentUserGuid), String(projectGUID));
  }, [currentUserGuid, projectGUID]);
  const readOnly = !canEditCurve;
  const canManageBaseline = useMemo(() => {
    if (!projectGUID) return false;
    if (!currentUserGuid) return false;
    return canAdminProject(String(currentUserGuid), String(projectGUID));
  }, [currentUserGuid, projectGUID]);

  // Audit trail + baseline lock state (read from project + instance config)
  const auditEnabled = useMemo(() => {
    if (!projectGuidNorm) return false;
    return !!(projOverride as any)?.audit_enabled;
  }, [projectGuidNorm, projOverride]);

  const [baselineLocked, setBaselineLocked] = useState(false);
  const [baselineLockedAt, setBaselineLockedAt] = useState<string | undefined>();
  useEffect(() => {
    if (!projectGuidNorm || !instanceGuidNorm) return;
    const kpiCfg = getProjectKpiConfigOverride(projectGuidNorm as any);
    const insts = (kpiCfg?.category_instances as any)?.finance || [];
    const inst = insts.find((i: any) =>
      String(i.instance_guid || "").toUpperCase() === instanceGuidNorm.toUpperCase()
    );
    setBaselineLocked(!!(inst as any)?.baseline_locked);
    setBaselineLockedAt((inst as any)?.baseline_locked_at || undefined);
  }, [projectGuidNorm, instanceGuidNorm, manageOpen, dataVersion]);

  // Effective window for this progress curve. Admins can set the window to any
  // range — including dates outside the project boundaries (e.g. phase tracking).
  const effectiveStartISO = useMemo(() => {
    const ps = startISO;
    if (!ps) return overrideStartISO || ps;
    if (canManageBaseline && overrideStartISO) {
      return overrideStartISO;
    }
    return ps;
  }, [startISO, overrideStartISO, canManageBaseline]);

  const effectiveEndISO = useMemo(() => {
    const pe = endISO;
    if (!pe) return overrideEndISO || pe;
    if (canManageBaseline && overrideEndISO) {
      return overrideEndISO;
    }
    return pe;
  }, [endISO, overrideEndISO, canManageBaseline]);

  // Keep "until" in lockstep with the effective curve end (project end or override end).
  useEffect(() => {
    const target = (effectiveEndISO || endISO || "").trim();
    if (!target) return;
    setUntilISO(target);
  }, [effectiveEndISO, endISO]);

  const [gridValue, setGridValue] = useState<{ cells: string[][] }>({
    cells: [[t('progress.reportingDate'), t('thermometer.baseline'), t('thermometer.forecast'), t('thermometer.actual')]],
  });
  const [clearFlash, setClearFlash] = useState(false);
  const [unlockedByClear, setUnlockedByClear] = useState(false);

  // Snapshot whether the modal opened with existing curve data. If so, lock
  // curve-generation controls until the admin/owner explicitly clears the grid.
  const [openedWithCurveData, setOpenedWithCurveData] = useState(false);

  const gridHasData = useMemo(() => {
    const cells = gridValue?.cells || [];
    return cells
      .slice(1)
      .some((r) => (r || []).some((c) => String(c || "").trim()));
  }, [gridValue]);

  const canEditActualsOnly = useMemo(() => {
    // Content editors can update Actuals but cannot touch reporting dates,
    // Baseline, Forecast, or curve-generation controls.
    return canEditCurve && !canManageBaseline;
  }, [canEditCurve, canManageBaseline]);

  // Seed the editable grid from the current series when the modal OPENS.
  // We track the previous manageOpen state so we only re-seed on the
  // false→true transition — NOT on every series/dataVersion change while
  // the modal is already open (which would wipe out in-progress edits).
  const prevManageOpenRef = useRef(false);
  useEffect(() => {
    const justOpened = manageOpen && !prevManageOpenRef.current;
    prevManageOpenRef.current = manageOpen;

    if (!manageOpen) return;
    // Only seed the grid on the false→true transition. While the modal
    // is already open, background data changes (cache invalidation,
    // SignalR events bumping dataVersion) must NOT overwrite user edits.
    if (!justOpened) return;

    // Each time the modal opens, re-lock generation controls if we opened with data.
    setUnlockedByClear(false);
    // Consider curve "present" if we have any reporting dates or any baseline/forecast/actual values.
    const openedWith = (series || []).some((r: any) => {
      const dateVal =
        r?.reporting_date ??
        r?.reportingDate ??
        r?.reporting_date_iso ??
        r?.date ??
        r?.reportingDateISO ??
        "";
      const hasDate = !!String(dateVal || "").trim();

      const baselineVal =
        r?.baseline_budget ??
        r?.baseline ??
        r?.baselineBudget ??
        r?.baseline_budget_pct;
      const forecastVal =
        r?.forecast_spend ??
        r?.forecast ??
        r?.forecastSpend ??
        r?.forecast_spend_pct;
      const actualVal =
        r?.actual_spend ?? r?.actual ?? r?.actualSpend ?? r?.actual_spend_pct;

      const hasAny = [baselineVal, forecastVal, actualVal].some(
        (v) => v !== null && v !== undefined && String(v).trim() !== "",
      );
      return hasDate || hasAny;
    });
    setOpenedWithCurveData(openedWith);

    // Default until date to project end date (bounded) when possible.
    if (!untilISO && effectiveEndISO) setUntilISO(String(effectiveEndISO));

    const rows = (series || []).map((r: any) => [
      formatDateUS(String(r.reporting_date || "")),
      r.baseline_budget === null || r.baseline_budget === undefined
        ? ""
        : String(r.baseline_budget),
      r.forecast_spend === null || r.forecast_spend === undefined
        ? ""
        : String(r.forecast_spend),
      r.actual_spend === null || r.actual_spend === undefined
        ? ""
        : String(r.actual_spend),
    ]);
    setGridValue({
      cells: [[t('progress.reportingDate'), t('thermometer.baseline'), t('thermometer.forecast'), t('thermometer.actual')], ...rows],
    });
  }, [manageOpen, projectGUID, dataVersion, series, untilISO, effectiveEndISO]);

  // When the modal opens on a project that already has curve data, lock
  // generation controls and admin actions until the admin/owner explicitly
  // clears the grid (local-only until Apply).
  const generationControlsLocked = openedWithCurveData && !unlockedByClear;

  useEffect(() => {
    if (!projectGUID) return;
    const meta = getBudgetCurveMetaForInstance(
      storageKey,
      instanceGuidNorm || undefined,
    );
    if (meta?.curve_type) setCurveType(meta.curve_type);
    if (meta?.source_project_guid)
      setCopyFromGuid(String(meta.source_project_guid));
    if ((meta as any)?.wbs_code) setWbsCode(String((meta as any).wbs_code));
    if ((meta as any)?.assigned_role_slot_guid) setAssignedRoleSlotGuid(String((meta as any).assigned_role_slot_guid));
    if (meta?.direction) setDirection(meta.direction);
    const sched = meta?.reporting_schedule as ReportingSchedule | undefined;
    if (sched?.cadence) setReportingCadence(sched.cadence);
    if (
      typeof sched?.interval === "number" &&
      Number.isFinite(sched.interval) &&
      sched.interval > 0
    )
      setRepeatEvery(sched.interval);
    if (sched?.until_date) setUntilISO(String(sched.until_date));
    if (Array.isArray((sched as any)?.by_weekday)) {
      const by = (sched as any).by_weekday as any[];
      const next = [false, false, false, false, false, false, false];
      for (const n of by) {
        const i = Number(n);
        if (Number.isFinite(i) && i >= 0 && i <= 6) next[i] = true;
      }
      // If empty, keep existing defaults.
      if (next.some(Boolean)) setWeekdays(next);
    }

    const os = String((meta as any)?.baseline_start_date_override || "").trim();
    const oe = String((meta as any)?.baseline_end_date_override || "").trim();
    if (os) setOverrideStartISO(os);
    if (oe) setOverrideEndISO(oe);
  }, [projectGUID]);

  // Highlight the "current" reporting row — the last data row whose date ≤ today.
  // Grid column 0 is in US format (MM/DD/YYYY); row 0 is the locked header.
  const currentReportingHighlight = useMemo(() => {
    const cells = gridValue?.cells || [];
    if (cells.length <= 1) return new Set<number>();
    const today = toISODate(new Date());
    let bestRow = -1;
    let bestISO = "";
    for (let r = 1; r < cells.length; r++) {
      const raw = String(cells[r]?.[0] || "").trim();
      if (!raw) continue;
      const iso = usToISO(raw) || (isISODate(raw) ? raw : null);
      if (!iso) continue;
      if (iso <= today && iso > bestISO) {
        bestISO = iso;
        bestRow = r;
      }
    }
    return bestRow >= 0 ? new Set([bestRow]) : new Set<number>();
  }, [gridValue]);

  const copyCandidates = useMemo(() => {
    if (!projectGUID || !projectRecord) return [] as any[];
    const all = getAllProjectsSeedPlusAdded() || [];
    const myGuid = String(projectGUID);

    const myCommunityGuid = String(
      (projectRecord as any)?.community_guid || "",
    );
    const myCommunityL3 = String(
      (projectRecord as any)?.community_l3 ||
        (projectRecord as any)?.Community ||
        "",
    );
    const myCommunityName = String(
      (projectRecord as any)?.community_name || "",
    );

    const pickOwnerGuid = (p: any): string => {
      const candidates = [
        p?.owner_user_guid,
        p?.owner_guid,
        p?.project_owner_user_guid,
        p?.created_by_user_guid,
        p?.created_by_guid,
        p?.user_guid,
        p?.user_GUID,
        p?.person_guid,
      ];
      for (const c of candidates) {
        const s = String(c || "").trim();
        if (s) return s;
      }
      return "";
    };

    const myOwnerGuid =
      pickOwnerGuid(projectRecord) || String(currentUserGuid || "");

    return (all as any[])
      .filter((p) => {
        const g = String(p?.project_GUID || p?.project_guid || "");
        if (!g || g === myGuid) return false;

        // 1) Same community (preferred)
        const cg = String(p?.community_guid || "");
        if (myCommunityGuid && cg && cg === myCommunityGuid) return true;

        // fallback community matching if no guids present
        const l3 = String(p?.community_l3 || p?.Community || "");
        const cn = String(p?.community_name || "");
        if (myCommunityName && cn && cn === myCommunityName) return true;
        if (myCommunityL3 && l3 && l3 === myCommunityL3) return true;

        // 2) Same owner/person
        const og = pickOwnerGuid(p);
        if (myOwnerGuid && og && og === myOwnerGuid) return true;

        return false;
      })
      .sort((a, b) =>
        String(a?.project_name || "").localeCompare(
          String(b?.project_name || ""),
        ),
      );
  }, [projectGUID, projectRecord, currentUserGuid]);

  useEffect(() => {
    // Default copy source to first candidate if blank.
    if (!copyFromGuid && copyCandidates.length) {
      const g = String(
        copyCandidates[0]?.project_GUID ||
          copyCandidates[0]?.project_guid ||
          "",
      );
      if (g) setCopyFromGuid(g);
    }
  }, [copyCandidates, copyFromGuid]);

  function parseIsoToLocalMs(iso: string): number {
    const [y, m, d] = String(iso || "")
      .split("-")
      .map(Number);
    if (!y || !m || !d) return NaN;
    return new Date(y, m - 1, d).getTime();
  }

  function isoFromMs(ms: number): string {
    const d = new Date(ms);
    return toISODate(d);
  }

  function addDays(ms: number, days: number) {
    return ms + days * 86400000;
  }

  function addDaysISO(iso: string, days: number): string {
    const ms = parseIsoToLocalMs(iso);
    if (!Number.isFinite(ms)) return "";
    return isoFromMs(addDays(ms, days));
  }

  /** Delegate to the shared reporting-date generator (single source of truth). */
  function buildReportingDates(
    start: string,
    end: string,
    cadence: ReportingCadence,
    interval: number,
    weekdaysOpt?: boolean[],
    monthlyModeOpt?: "day" | "first" | "last",
    monthlyDayOpt?: number,
    monthlyWeekdayOpt?: number,
  ): string[] {
    return buildReportingDatesShared(
      start, end, cadence, interval,
      weekdaysOpt, monthlyModeOpt, monthlyDayOpt, monthlyWeekdayOpt,
    );
  }

  // clamp01Local replaced by imported clamp01 from thermometerUtils
  const clamp01Local = clamp01;

  function curveValue01(t: number, type: FinanceCurveType): number {
    const x = clamp01Local(t);
    switch (type) {
      case "linear":
        return x;
      case "front_loaded": {
        // faster early progress
        const a = 0.65;
        return Math.pow(x, a);
      }
      case "back_loaded": {
        const a = 1.6;
        return Math.pow(x, a);
      }
      case "sigmoidal": {
        // logistic-ish S curve normalized 0..1
        const k = 10;
        const y = 1 / (1 + Math.exp(-k * (x - 0.5)));
        const y0 = 1 / (1 + Math.exp(-k * (0 - 0.5)));
        const y1 = 1 / (1 + Math.exp(-k * (1 - 0.5)));
        return (y - y0) / (y1 - y0);
      }
      default:
        return x;
    }
  }

  function inferScaleFactorLocal(rows: any[]): number {
    let maxV = 0;
    for (const r of rows || []) {
      maxV = Math.max(maxV, Number(r?.baseline_budget ?? 0));
    }
    return maxV > 1.5 ? 100 : 1;
  }

  function buildNormalizedBaselineFromProject(
    sourceGuid: string,
  ): { t: number; v: number }[] {
    if (!sourceGuid) return [];
    let ov = getFinanceCostSeriesForInstance(
      sourceGuid,
      instanceGuidNorm || undefined,
    );
    // If this chart instance does not exist on the source project, fall back to the source project's legacy/overall series.
    if ((!ov || ov.length === 0) && instanceGuidNorm) {
      ov = getFinanceCostSeriesForInstance(sourceGuid, undefined);
    }
    const rows = ov && ov.length ? ov : [];

    if (!rows.length) return [];
    const sorted = [...rows].sort((a, b) =>
      String(a.reporting_date).localeCompare(String(b.reporting_date)),
    );
    const sMs = parseIsoToLocalMs(sorted[0].reporting_date);
    const eMs = parseIsoToLocalMs(sorted[sorted.length - 1].reporting_date);
    if (!Number.isFinite(sMs) || !Number.isFinite(eMs) || eMs === sMs)
      return [];

    const scale = inferScaleFactorLocal(sorted);
    const lastVal =
      Number(sorted[sorted.length - 1]?.baseline_budget ?? 0) / scale;
    const denom = lastVal > 0 ? lastVal : 1;

    return sorted.map((r) => {
      const ms = parseIsoToLocalMs(r.reporting_date);
      const t = clamp01Local((ms - sMs) / (eMs - sMs));
      const v = clamp01Local(Number(r.baseline_budget ?? 0) / scale / denom);
      return { t, v };
    });
  }

  function interp01(points: { t: number; v: number }[], t: number): number {
    const x = clamp01Local(t);
    if (!points.length) return x;
    const ps = [...points].sort((a, b) => a.t - b.t);
    if (x <= ps[0].t) return ps[0].v;
    if (x >= ps[ps.length - 1].t) return ps[ps.length - 1].v;
    for (let i = 1; i < ps.length; i++) {
      const a = ps[i - 1];
      const b = ps[i];
      if (x >= a.t && x <= b.t) {
        const span = b.t - a.t;
        const u = span ? (x - a.t) / span : 0;
        return a.v + u * (b.v - a.v);
      }
    }
    return x;
  }

  const generateBaseline = () => {
    if (!projectGUID) return;
    if (!canManageBaseline) return;
    if (!startISO || !endISO) {
      showAlert(
        "Please set Project Start Date and End Date (Project Settings → Basics) before baselining.",
      );
      return;
    }

    // Enforce bounded window (phase-aware)
    const untilRaw = (effectiveEndISO || endISO || "").trim();
    let until = untilRaw;
    if (effectiveEndISO && until > effectiveEndISO) until = effectiveEndISO;
    if (effectiveStartISO && until < effectiveStartISO)
      until = effectiveStartISO;
    if (!until) {
      showAlert("Please choose an 'Until' date (bounded reporting window).");
      return;
    }
    const dates = buildReportingDates(
      effectiveStartISO || startISO,
      until,
      reportingCadence,
      repeatEvery,
      weekdays,
      monthlyMode,
      monthlyDayOfMonth,
      monthlyWeekday,
    );
    if (dates.length < 2) {
      showAlert(
        "Please ensure Start/End dates are valid and create at least two reporting dates.",
      );
      return;
    }

    let baseline01ByT: ((t: number) => number) | null = null;

    if (curveType === "manual") {
      // Manual mode: generate empty rows (dates only) for hand-entry
      baseline01ByT = null;
    } else if (curveType === "copy") {
      const pts = buildNormalizedBaselineFromProject(copyFromGuid);
      if (!pts.length) {
        showAlert("Could not read a baseline curve from the selected project.");
        return;
      }
      baseline01ByT = (t) => interp01(pts, t);
    } else if (curveType === "custom") {
      // Temporary: treat "custom" as linear until the custom curve editor exists.
      baseline01ByT = (t) => curveValue01(t, "linear");
    } else {
      baseline01ByT = (t) => curveValue01(t, curveType);
    }

    const sMs = parseIsoToLocalMs(effectiveStartISO || startISO);
    const eMs = parseIsoToLocalMs(until);

    const rows: FinanceCostPoint[] = dates.map((d) => {
      if (curveType === "manual") {
        // Manual: empty rows for user to fill in
        return {
          project_guid: String(projectGUID),
          reporting_date: d,
          baseline_budget: null,
          actual_spend: null,
          forecast_spend: null,
        };
      }
      const ms = parseIsoToLocalMs(d);
      const t = clamp01Local((ms - sMs) / (eMs - sMs));
      const b01 = clamp01Local(baseline01ByT ? baseline01ByT(t) : t);
      const value01 = direction === "decreasing" ? 1 - b01 : b01;
      const b = Math.round(value01 * 1000) / 10; // one decimal pct
      return {
        project_guid: String(projectGUID),
        reporting_date: d,
        baseline_budget: b,
        actual_spend: null,
        forecast_spend: b,
      };
    });

    // ensure last is correct end value (skip for manual mode)
    if (curveType !== "manual") {
      const endVal = direction === "decreasing" ? 0 : 100;
      rows[rows.length - 1].baseline_budget = endVal;
      rows[rows.length - 1].forecast_spend = endVal;
    }

    // Preview only: populate the editable grid, but DO NOT persist until Apply.
    const gridRows = rows.map((r) => [
      formatDateUS(r.reporting_date || ""),
      r.baseline_budget === null || r.baseline_budget === undefined
        ? ""
        : String(r.baseline_budget),
      r.forecast_spend === null || r.forecast_spend === undefined
        ? ""
        : String(r.forecast_spend),
      r.actual_spend === null || r.actual_spend === undefined
        ? ""
        : String(r.actual_spend),
    ]);
    setGridValue({
      cells: [[t('progress.reportingDate'), t('thermometer.baseline'), t('thermometer.forecast'), t('thermometer.actual')], ...gridRows],
    });
  };

  const initializeCurve = () => {
    if (!projectGUID) return;
    if (!canEditCurve) return;

    if (!startISO || !endISO) {
      showAlert(
        "Please set Project Start Date and End Date (Project Settings → Basics) before initializing reporting dates.",
      );
      return;
    }

    // Enforce bounded window (phase-aware)
    const untilRaw = (effectiveEndISO || endISO || "").trim();
    let until = untilRaw;
    if (effectiveEndISO && until > effectiveEndISO) until = effectiveEndISO;
    if (effectiveStartISO && until < effectiveStartISO)
      until = effectiveStartISO;
    if (!until) {
      showAlert("Please choose an 'Until' date (bounded reporting window).");
      return;
    }
    const dates = buildReportingDates(
      effectiveStartISO || startISO,
      until,
      reportingCadence,
      repeatEvery,
      weekdays,
      monthlyMode,
      monthlyDayOfMonth,
      monthlyWeekday,
    );
    if (dates.length < 2) {
      showAlert(
        "Please ensure Start/End dates are valid and create at least two reporting dates.",
      );
      return;
    }

    const rows: FinanceCostPoint[] = dates.map((d) => ({
      project_guid: String(projectGUID),
      instance_guid: instanceGuidNorm || undefined,
      reporting_date: d,
      baseline_budget: null,
      forecast_spend: null,
      actual_spend: null,
    }));

    setFinanceCostSeriesForInstance(
      storageKey,
      instanceGuidNorm || undefined,
      rows,
    );
    setBudgetCurveMetaForInstance(storageKey, instanceGuidNorm || undefined, {
      curve_type: curveType,
      source_project_guid: curveType === "copy" ? copyFromGuid : undefined,
      direction,
      wbs_code: wbsCode || undefined,
      assigned_role_slot_guid: assignedRoleSlotGuid || undefined,
      reporting_schedule: {
        cadence: reportingCadence,
        interval: repeatEvery,
        until_date: until,
        by_weekday: weekdays
          .map((on, idx) => (on ? idx : -1))
          .filter((x) => x >= 0),
      },
      baseline_start_date: effectiveStartISO || startISO,
      baseline_end_date: until,
      baseline_start_date_override: overrideStartISO || undefined,
      baseline_end_date_override: overrideEndISO || undefined,
      suppress_seed: false as any,
    });

    setDataVersion((v) => v + 1);
    try {
      window.dispatchEvent(new Event("l4e_finance_cost_changed"));
      window.dispatchEvent(new CustomEvent("l4e_project_updated", { detail: { project_guid: projectGUID, fields: ["finance"] } }));
    } catch {}
  };

  const requireWeeklyDaysOrWarn = (): boolean => {
    if (reportingCadence !== "weekly") return true;
    if (weekdays.some(Boolean)) return true;
    setShowWeekdayWarning(true);
    return false;
  };

  const handleInitializeDates = () => {
    if (!requireWeeklyDaysOrWarn()) return;
    initializeCurve();
  };

  const handleGenerateBaseline = () => {
    if (!requireWeeklyDaysOrWarn()) return;
    generateBaseline();
  };

  const clearBaseline = () => {
    if (!projectGUID) return;
    showConfirm(
      "Clear the curve grid? This will not be saved until you click Apply.",
      () => {
        // Unlock generation controls for this session (not persisted until Apply).
        setUnlockedByClear(true);

        // Clear the editable grid immediately (but do NOT persist until Apply).
        setGridValue({
          cells: [[t('progress.reportingDate'), t('thermometer.baseline'), t('thermometer.forecast'), t('thermometer.actual')]],
        });

        // Small visual cue.
        setClearFlash(true);
        window.setTimeout(() => setClearFlash(false), 900);
      },
    );
  };

  // ── Populate Actuals helpers ──────────────────────────────────────

  /**
   * Backfill actuals with baseline values for all reporting dates up to today.
   * Leaves dates after today untouched.
   */
  const backfillActualsFromBaseline = () => {
    const cells = gridValue?.cells || [];
    if (cells.length < 2) {
      showAlert("Generate a baseline first so there are rows to backfill.");
      return;
    }
    const todayISO = new Date().toISOString().slice(0, 10);
    const header = cells[0];
    const updated = cells.slice(1).map((row) => {
      const dateUS = String(row?.[0] || "").trim();
      const iso = usToISO(dateUS);
      if (!iso) return [...row];
      const baseline = String(row?.[1] ?? "").trim();
      if (iso <= todayISO && baseline !== "") {
        // Copy baseline → actual
        return [row[0], row[1], row[2], baseline];
      }
      return [...row];
    });
    setGridValue({ cells: [header, ...updated] });
  };

  /**
   * Simulate actuals with realistic variance from baseline.
   * – Occasionally hits yellow tolerance (~10% behind) and red (~20% behind).
   * – Actuals are monotonically non-decreasing (each ≥ previous).
   * – Uses a seeded random walk so results are reproducible per session.
   */
  const simulateActualsWithVariance = () => {
    const cells = gridValue?.cells || [];
    if (cells.length < 2) {
      showAlert("Generate a baseline first so there are rows to simulate.");
      return;
    }
    const header = cells[0];
    const dataRows = cells.slice(1);

    // Simple seeded pseudo-random (Mulberry32)
    let seed = 42 + dataRows.length;
    const rand = () => {
      seed |= 0;
      seed = (seed + 0x6d2b79f5) | 0;
      let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
      t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };

    let prevActual = direction === "decreasing" ? 100 : 0;
    const updated = dataRows.map((row) => {
      const baseline = String(row?.[1] ?? "").trim();
      if (baseline === "") return [...row];
      const bVal = Number(baseline);
      if (!Number.isFinite(bVal)) return [...row];

      // Random deviation: ~60% on-track, ~25% yellow zone, ~15% red zone
      const roll = rand();
      let deviationFrac: number;
      if (roll < 0.6) {
        // On track: 0–8% deviation from baseline
        deviationFrac = rand() * 0.08;
      } else if (roll < 0.85) {
        // Yellow zone: 8–18% deviation
        deviationFrac = 0.08 + rand() * 0.1;
      } else {
        // Red zone: 18–30% deviation
        deviationFrac = 0.18 + rand() * 0.12;
      }

      let actual: number;
      if (direction === "decreasing") {
        // Decreasing: actual should be HIGHER than baseline (lagging behind reduction)
        actual = Math.min(100, bVal + bVal * deviationFrac);
        // Monotonically non-increasing for decreasing curves → non-decreasing check inverted
        actual = Math.min(prevActual, actual);
        // Floor at 0
        actual = Math.max(0, actual);
        prevActual = actual;
      } else {
        // Increasing: actual should be LOWER than baseline (lagging behind)
        actual = Math.max(0, bVal - bVal * deviationFrac);
        // Monotonically non-decreasing
        actual = Math.max(prevActual, actual);
        // Cap at 100
        actual = Math.min(100, actual);
        prevActual = actual;
      }

      return [row[0], row[1], row[2], String(Math.round(actual * 100) / 100)];
    });

    setGridValue({ cells: [header, ...updated] });
  };

  // ── Apply handler with full pipeline status logging ──
  const applyGridBaseline = async () => {
    // Clear previous log and start fresh
    setStatusLog([]);

    if (!projectGUID) {
      pushStatus(
        "error",
        "No projectGUID — aborting. Component has no project context.",
      );
      return;
    }

    pushStatus(
      "info",
      `▶ Apply started — projectGUID=${projectGUID}, storageKey=${storageKey}, instanceGuid=${instanceGuidNorm || "(none)"}`,
    );

    // ── Step 1: Parse grid rows ──
    const cells = gridValue?.cells || [];
    const rowsIn = cells
      .slice(1)
      .filter((r) => (r || []).some((c) => String(c || "").trim()));

    pushStatus(
      "info",
      `Step 1 — Grid has ${cells.length} total rows, ${rowsIn.length} non-empty data rows`,
    );

    type ParsedRow = {
      d: string;
      baseline: number | null;
      forecast: number | null;
      actual: number | null;
    };
    const parsed: ParsedRow[] = [];

    for (const r of rowsIn) {
      const dIn = String(r?.[0] || "").trim();
      if (!dIn) continue;
      const d = usToISO(dIn);
      if (!d) {
        pushStatus(
          "error",
          `Step 1 — Invalid date format: "${dIn}". Expected MM/DD/YYYY.`,
        );
        showAlert(
          `Invalid date format: ${dIn}. Use MM/DD/YYYY (e.g., 03/01/2026).`,
        );
        return;
      }

      const bRaw = String(r?.[1] ?? "").trim();
      const fRaw = String(r?.[2] ?? "").trim();
      const aRaw = String(r?.[3] ?? "").trim();

      const b = bRaw === "" ? null : Number(bRaw);
      const f = fRaw === "" ? null : Number(fRaw);
      const a = aRaw === "" ? null : Number(aRaw);

      parsed.push({
        d,
        baseline: b !== null && Number.isFinite(b) ? b : null,
        forecast: f !== null && Number.isFinite(f) ? f : null,
        actual: a !== null && Number.isFinite(a) ? a : null,
      });
    }

    pushStatus(
      "info",
      `Step 1 — Parsed ${parsed.length} valid rows: ${parsed.map((p) => p.d).join(", ")}`,
    );

    // ── Step 2: Handle empty grid (clear) ──
    if (!parsed.length) {
      pushStatus("warn", "Step 2 — No parsed rows. Grid is empty.");
      if (!canManageBaseline) {
        pushStatus("info", "Step 2 — Non-admin user, closing panel (no-op).");
        setManageOpen(false);
        return;
      }
      pushStatus(
        "info",
        "Step 2 — Admin clearing curve. Writing empty series to store.",
      );
      setFinanceCostSeriesForInstance(
        storageKey,
        instanceGuidNorm || undefined,
        [],
      );
      const until = (effectiveEndISO || endISO || "").trim();
      setBudgetCurveMetaForInstance(
        storageKey,
        instanceGuidNorm || undefined,
        {
          curve_type: curveType,
          source_project_guid: curveType === "copy" ? copyFromGuid : undefined,
          direction,
          wbs_code: wbsCode || undefined,
      assigned_role_slot_guid: assignedRoleSlotGuid || undefined,
          reporting_schedule: {
            cadence: reportingCadence,
            interval: repeatEvery,
            until_date: until || undefined,
          },
          baseline_start_date:
            (effectiveStartISO || startISO || "").trim() || undefined,
          baseline_end_date: until || undefined,
          suppress_seed: true,
        },
      );

      // Delete ALL existing records from the API so hydration doesn't restore
      // stale data after the local grace window expires.
      try {
        const cleanup = await deleteBudgetSeriesPointsByDateRange(
          String(projectGUID),
          instanceGuidNorm || undefined,
          "9999-12-31", // keepStartDate — nothing will match
          "9999-12-31", // keepEndDate   — so everything is deleted
        );
        pushStatus("info", `Step 2 — API records deleted: ${cleanup.deletedCount}`);
      } catch (err: any) {
        pushStatus("warn", `Step 2 — API delete failed (non-fatal): ${err?.message || "Unknown error"}`);
      }

      setDataVersion((v) => v + 1);
      try {
        window.dispatchEvent(new Event("l4e_finance_cost_changed"));
      window.dispatchEvent(new CustomEvent("l4e_project_updated", { detail: { project_guid: projectGUID, fields: ["finance"] } }));
      } catch {}
      pushStatus("success", "Step 2 — Curve cleared. Done.");
      setManageOpen(false);
      return;
    }

    // ── Step 3: Merge parsed rows with existing data ──
    const existing =
      getFinanceCostSeriesForInstance(
        storageKey,
        instanceGuidNorm || undefined,
      ) || [];
    pushStatus(
      "info",
      `Step 3 — In-memory store has ${existing.length} existing rows for key="${storageKey}"`,
    );

    const desiredDates = new Set(parsed.map((p) => p.d));
    const byDate: Record<string, FinanceCostPoint> = {};

    for (const r of existing) {
      const rd = String((r as any).reporting_date || "").trim();
      if (!isISODate(rd)) continue;
      if (canManageBaseline && desiredDates.size && !desiredDates.has(rd))
        continue;
      byDate[rd] = {
        ...(r as any),
        project_guid: String(projectGUID),
        instance_guid: instanceGuidNorm || undefined,
      } as any;
    }

    for (const p of parsed) {
      const prev =
        byDate[p.d] ||
        ({
          project_guid: String(projectGUID),
          instance_guid: instanceGuidNorm || undefined,
          reporting_date: p.d,
          baseline_budget: null,
          actual_spend: null,
          forecast_spend: null,
        } as FinanceCostPoint);

      const next: FinanceCostPoint = { ...prev };
      if (canManageBaseline) {
        next.baseline_budget = p.baseline;
        next.forecast_spend = p.forecast;
      }
      if (canEditCurve) {
        next.actual_spend = p.actual;
      }
      byDate[p.d] = next;
    }

    const out = Object.values(byDate).sort((a, b) =>
      String(a.reporting_date).localeCompare(String(b.reporting_date)),
    );
    pushStatus(
      "info",
      `Step 3 — Merged: ${out.length} rows ready. canManageBaseline=${canManageBaseline}, canEditCurve=${canEditCurve}`,
    );

    // ── Step 3.5: Record count safety limit ──
    const MAX_ROWS_PER_INSTANCE = 2000;
    if (out.length > MAX_ROWS_PER_INSTANCE) {
      pushStatus("error", t('thermometer.rowLimitExceeded', { count: String(out.length), max: String(MAX_ROWS_PER_INSTANCE) }));
      return;
    }
    if (out.length > MAX_ROWS_PER_INSTANCE * 0.9) {
      pushStatus("warn", t('thermometer.rowLimitWarning', { count: String(out.length), max: String(MAX_ROWS_PER_INSTANCE) }));
    }

    // ── Step 4: Update in-memory store (for immediate chart re-render) ──
    // skipApiWrite: true because Step 5 does an explicit awaited batch upsert.
    setFinanceCostSeriesForInstance(
      storageKey,
      instanceGuidNorm || undefined,
      out,
      { skipApiWrite: true },
    );
    pushStatus(
      "info",
      `Step 4 — In-memory store updated (setFinanceCostSeriesForInstance). ${out.length} rows in store.`,
    );

    // ── Step 4.5: Delete ALL existing records before writing new ones ──
    // The grid is the source of truth. Old records with dates that don't match
    // the new curve (e.g., from a previous cadence or date range) must be removed
    // before the batch upsert, otherwise they survive and intermix on refresh.
    try {
      const preclean = await deleteBudgetSeriesPointsByDateRange(
        String(projectGUID),
        instanceGuidNorm || undefined,
        "9999-12-31", // keepStartDate — nothing matches
        "9999-12-31", // keepEndDate   — so everything is deleted
      );
      if (preclean.deletedCount > 0) {
        pushStatus("info", `Step 4.5 — Pre-cleanup: deleted ${preclean.deletedCount} existing records before upsert.`);
      }
    } catch (err: any) {
      pushStatus("warn", `Step 4.5 — Pre-cleanup failed (non-fatal): ${err?.message || "Unknown error"}`);
    }

    // ── Step 5: DIRECT API call — awaited, NOT fire-and-forget ──
    const apiPoints: CreateBudgetSeriesPointRequest[] = out.map((r) => ({
      projectId: String(r.project_guid || projectGUID),
      instanceGuid: r.instance_guid || undefined,
      reportingDate: r.reporting_date,
      baselineBudget: r.baseline_budget ?? undefined,
      actualSpend: r.actual_spend ?? undefined,
      forecastSpend: r.forecast_spend ?? undefined,
    }));

    pushStatus(
      "info",
      `Step 5 — Calling POST /api/FinanceSeriesPoints/batch with ${apiPoints.length} points`,
    );
    pushStatus(
      "info",
      `Step 5 — First point: projectId=${apiPoints[0]?.projectId}, instanceGuid=${apiPoints[0]?.instanceGuid || "null"}, date=${apiPoints[0]?.reportingDate}, baseline=${apiPoints[0]?.baselineBudget}, actual=${apiPoints[0]?.actualSpend}, forecast=${apiPoints[0]?.forecastSpend}`,
    );

    const apiStartMs = Date.now();
    try {
      const result = await batchUpsertBudgetSeriesPoints(apiPoints);
      const elapsed = Date.now() - apiStartMs;
      pushStatus(
        "success",
        `Step 5 — API SUCCESS (${elapsed}ms) — ${result.length} rows upserted. IDs: ${result
          .slice(0, 3)
          .map((r: any) => r.id || "?")
          .join(", ")}${result.length > 3 ? "…" : ""}`,
      );
    } catch (err: any) {
      const elapsed = Date.now() - apiStartMs;
      pushStatus(
        "error",
        `Step 5 — API FAILED (${elapsed}ms) — HTTP ${err?.status || "?"}: ${err?.message || "Unknown error"}`,
      );
      if (err?.body) {
        pushStatus(
          "error",
          `Step 5 — Response body: ${JSON.stringify(err.body)}`,
        );
      }
      pushStatus(
        "error",
        `Step 5 — Full error: ${JSON.stringify(err, Object.getOwnPropertyNames(err || {}))}`,
      );
    }

    // ── Step 5.5: Clean up out-of-range records in DB ──
    // When audit is enabled AND baseline is locked, use soft-delete (versioning).
    // Otherwise, hard-delete stale records.
    const cleanupStart = (effectiveStartISO || startISO || "").trim();
    const cleanupEnd = (effectiveEndISO || endISO || "").trim();
    if (cleanupStart && cleanupEnd) {
      const useSoftDelete = auditEnabled && baselineLocked;
      try {
        const cleanup = await deleteBudgetSeriesPointsByDateRange(
          String(projectGUID),
          instanceGuidNorm || undefined,
          cleanupStart,
          cleanupEnd,
          useSoftDelete ? { softDelete: true } : undefined,
        );
        if (cleanup.deletedCount > 0) {
          pushStatus("info", t('thermometer.outOfRangeRemoved', {
            count: String(cleanup.deletedCount),
            start: cleanupStart,
            end: cleanupEnd,
          }) + (useSoftDelete ? " (versioned — audit trail preserved)" : ""));
        }
      } catch (cleanupErr: any) {
        // Non-fatal: stale records remain but aren't displayed (Phase 1A filter)
        pushStatus("warn", `Step 5.5 — Cleanup failed (non-fatal): ${cleanupErr?.message || "Unknown error"}`);
      }
    }

    // ── Step 6: Save curve meta (if admin) ──
    if (canManageBaseline) {
      const until = (effectiveEndISO || endISO || "").trim();
      setBudgetCurveMetaForInstance(
        storageKey,
        instanceGuidNorm || undefined,
        {
          curve_type: curveType,
          source_project_guid: curveType === "copy" ? copyFromGuid : undefined,
          direction,
          wbs_code: wbsCode || undefined,
      assigned_role_slot_guid: assignedRoleSlotGuid || undefined,
          reporting_schedule: {
            cadence: reportingCadence,
            interval: repeatEvery,
            until_date: until || undefined,
          },
          baseline_start_date: startISO || undefined,
          baseline_end_date: until || undefined,
          suppress_seed: false as any,
        },
      );
      pushStatus(
        "info",
        "Step 6 — Curve meta saved (setBudgetCurveMetaForInstance).",
      );
    } else {
      pushStatus("info", "Step 6 — Skipped curve meta (not admin).");
    }

    // ── Step 7: Notify and close ──
    setDataVersion((v) => v + 1);
    try {
      window.dispatchEvent(new Event("l4e_finance_cost_changed"));
      window.dispatchEvent(new CustomEvent("l4e_project_updated", { detail: { project_guid: projectGUID, fields: ["finance"] } }));
    } catch {}
    pushStatus(
      "success",
      "Step 7 — Done. Event dispatched, dataVersion incremented, panel closing.",
    );
    // Don't close immediately so user can read the log
    // setManageOpen(false);
  };

  // SVG coordinate system
  const W = 860;
  const H = 220;
  const padL = 44;
  const padR = 12;
  const padT = 12;
  // When legend (+ x-axis labels) is visible, keep room for labels;
  // otherwise shrink padding so chart bottom aligns with thermometer bottom.
  const padB = showLegend ? 34 : 12;
  const innerW = W - padL - padR;
  const innerH = H - padT - padB;

  const msMinMax = useMemo(() => {
    if (!series.length) return { min: NaN, max: NaN };
    const ms = series
      .map((r) => parseISODateToMs(r.reporting_date))
      .filter(Number.isFinite) as number[];
    if (!ms.length) return { min: NaN, max: NaN };
    return { min: Math.min(...ms), max: Math.max(...ms) };
  }, [series]);

  // Zoom/pan window (x domain)
  const [view, setView] = useState<{ min: number; max: number } | null>(null);

  useEffect(() => {
    // Reset zoom when switching projects or when data range changes.
    if (Number.isFinite(msMinMax.min) && Number.isFinite(msMinMax.max)) {
      setView({ min: msMinMax.min, max: msMinMax.max });
    } else {
      setView(null);
    }
  }, [projectGUID, msMinMax.min, msMinMax.max]);

  const viewMinMax = view ?? msMinMax;

  const xForMs = (ms: number) => {
    const { min, max } = viewMinMax;
    if (
      !Number.isFinite(ms) ||
      !Number.isFinite(min) ||
      !Number.isFinite(max) ||
      max === min
    )
      return padL;
    const t = (ms - min) / (max - min);
    return padL + clamp01(t) * innerW;
  };

  const xForRow = (r: FinanceCostPoint) =>
    xForMs(parseISODateToMs(r.reporting_date));

  const yForValue01 = (v01: number) => {
    const v = clamp01(v01);
    // 0 at bottom, 1 at top
    return padT + (1 - v) * innerH;
  };

  // DEV-1010 (a) + (b): pivot the forecast line and confidence band on
  // observed spend. Mirrors the ScheduleThermometer logic — see that
  // file for the full algorithm rationale.
  //
  // (a) — boundary anchor: override the prepended row's `forecast_spend`
  // to equal its `actual_spend` so the forecast line starts at "today's
  // actual spend" rather than at the planned-spend value.
  //
  // (b) — curve translation forward: shift each future row's
  // `forecast_spend` by `offset = actualSpendAtToday - baselineSpendAtToday`.
  // Preserves the spend-curve shape, just elevates (overrunning) or
  // depresses (underspending) the future trajectory by the current
  // offset.
  //
  // Clamp differs from Schedule: budget has no upper cap (a project CAN
  // overspend its baseline, and the chart should depict that honestly).
  // Lower bound clamped at 0 to prevent nonsense negative spend.
  // Band-width refinement deferred — see DEV-1010 follow-up note.
  const forecastRowsConnected = (() => {
    if (!actualRows.length || !forecastRows.length) return forecastRows;
    const last = actualRows[actualRows.length - 1];
    const actualAtToday = last.actual_spend;
    const baselineAtToday = last.baseline_budget;

    // (a) — anchor the boundary row to the actual spend.
    const anchored = {
      ...last,
      forecast_spend: actualAtToday ?? last.forecast_spend,
    };

    // (b) — translate future rows if we have a clean offset.
    const canTranslate =
      Number.isFinite(actualAtToday as number) &&
      Number.isFinite(baselineAtToday as number);
    if (!canTranslate) return [anchored, ...forecastRows];

    const offset =
      (actualAtToday as number) - (baselineAtToday as number);
    const projected = forecastRows.map((r) => {
      const b = r.baseline_budget;
      if (!Number.isFinite(b as number)) return r;
      const shifted = Math.max(0, (b as number) + offset);
      return { ...r, forecast_spend: shifted };
    });

    return [anchored, ...projected];
  })();

  const baselinePath = buildPath(
    baselineRows,
    (r) => r.baseline_budget,
    xForRow,
    yForValue01,
    effectiveScaleFactor,
  );

  const actualPath = buildPath(
    actualRows,
    (r) => r.actual_spend,
    xForRow,
    yForValue01,
    effectiveScaleFactor,
  );

  const forecastPath = buildPath(
    forecastRowsConnected,
    (r) => r.forecast_spend,
    xForRow,
    yForValue01,
    effectiveScaleFactor,
  );

  // Confidence band around forecast (heuristic): use recent actual-vs-baseline volatility.
  const forecastBandHalfWidth01ByDate = useForecastBandWidths(
    series,
    (r) => r.actual_spend,
    (r) => r.baseline_budget,
    effectiveScaleFactor,
    todayISO,
  );

  const forecastBandPath = buildBandPath(
    forecastRowsConnected,
    (r) => r.forecast_spend,
    (r) => forecastBandHalfWidth01ByDate.get(r.reporting_date) ?? 0.06,
    xForRow,
    yForValue01,
    effectiveScaleFactor,
  );

  // Axis ticks
  const yTicks = isAbsoluteView
    ? [
        { v01: 1, label: formatCurrencyCompact(effectiveScaleFactor, budgetCurrencyCode) },
        { v01: 0.5, label: formatCurrencyCompact(effectiveScaleFactor * 0.5, budgetCurrencyCode) },
        { v01: 0, label: `${currencySymbol}0` },
      ]
    : [
        { v01: 1, label: "100%" },
        { v01: 0.5, label: "50%" },
        { v01: 0, label: "0%" },
      ];

  const xTickLabels = (() => {
    // Short date format: "Mon 'YY" — no day of month, matches CPI history
    const months = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    const fmt = (d?: string | null) => {
      if (!d) return "";
      const parts = String(d).trim().split("-");
      if (parts.length !== 3) return String(d);
      const [y, mRaw] = parts;
      const mNum = Number(mRaw);
      if (!y || !Number.isFinite(mNum)) return String(d);
      const mon = months[Math.max(1, Math.min(12, mNum)) - 1];
      return `${mon} '${String(y).slice(2)}`;
    };
    if (series.length < 2)
      return {
        left: fmt(latest?.reporting_date ?? ""),
        mid: "",
        right: fmt(latest?.reporting_date ?? ""),
      };
    const left = fmt(series[0].reporting_date);
    const right = fmt(series[series.length - 1].reporting_date);
    const mid = fmt(series[Math.floor(series.length / 2)].reporting_date);
    return { left, mid, right };
  })();

  // Header meta
  const actualPctText =
    actualLatest && series.length
      ? fmtValue(Number(actualLatest.actual_spend))
      : isAbsoluteView ? `${currencySymbol}0` : "0%";
  const endDate = actualLatest?.reporting_date ?? "";
  const endDateAsOf = fmtAsOfDate(endDate);

  // Track chart panel width for adaptive x-axis labels
  const chartPanelRef = useRef<HTMLDivElement | null>(null);
  const [chartPanelWidth, setChartPanelWidth] = useState(600);
  useEffect(() => {
    const el = chartPanelRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      for (const e of entries) setChartPanelWidth(e.contentRect.width);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Interactivity (tooltip)
  const svgRef = useRef<SVGSVGElement | null>(null);
  const [plotPadPx, setPlotPadPx] = useState<{ top: number; bottom: number }>({
    top: 0,
    bottom: 0,
  });

  // Keep the left thermometer aligned to the chart's 0–100 plot area (inside the SVG padding).
  useEffect(() => {
    const el = svgRef.current;
    if (!el) return;

    const compute = () => {
      const rect = el.getBoundingClientRect();
      if (!rect || rect.height <= 0) return;

      // SVG uses preserveAspectRatio="none" so Y scales linearly.
      const topPx = (rect.height * padT) / H;
      const bottomPx = (rect.height * padB) / H;

      setPlotPadPx((prev) => {
        // Avoid re-render churn for tiny deltas.
        if (
          Math.abs(prev.top - topPx) < 0.5 &&
          Math.abs(prev.bottom - bottomPx) < 0.5
        ) {
          return prev;
        }
        return { top: topPx, bottom: bottomPx };
      });
    };

    compute();

    const ResizeObserverCtor = (window as any).ResizeObserver;
    if (ResizeObserverCtor) {
      const ro = new ResizeObserverCtor(() => compute());
      ro.observe(el);
      return () => ro.disconnect();
    }

    // Fallback: listen to window resize if ResizeObserver is unavailable.
    const onWin = () => compute();
    window.addEventListener("resize", onWin);
    return () => window.removeEventListener("resize", onWin);
  }, [padT, padB, H]);

  const [hoverIndex, setHoverIndex] = useState<number | null>(null);
  const [hoverClient, setHoverClient] = useState<{
    x: number;
    y: number;
  } | null>(null);

  // Drag pan state
  const dragRef = useRef<{
    isDown: boolean;
    startClientX: number;
    startMin: number;
    startMax: number;
  } | null>(null);

  const seriesMs = useMemo(
    () => series.map((r) => parseISODateToMs(r.reporting_date)),
    [series],
  );

  const nearestIndexForMs = (ms: number) => {
    // Find nearest by time (not x pixels).
    let best = 0;
    let bestDist = Infinity;
    for (let i = 0; i < seriesMs.length; i++) {
      const v = seriesMs[i];
      if (!Number.isFinite(v)) continue;
      const d = Math.abs(v - ms);
      if (d < bestDist) {
        bestDist = d;
        best = i;
      }
    }
    return best;
  };

  const onMouseMove: React.MouseEventHandler<SVGSVGElement> = (e) => {
    if (!svgRef.current || !series.length) return;

    const rect = svgRef.current.getBoundingClientRect();
    const px = e.clientX - rect.left;
    const py = e.clientY - rect.top;

    // Convert pixel x in the element to SVG viewBox x.
    const svgX = (px / rect.width) * W;

    // Map svgX to an approximate ms value, then snap to nearest series row.
    const { min, max } = viewMinMax;
    if (!Number.isFinite(min) || !Number.isFinite(max) || max === min) return;

    const t = clamp01((svgX - padL) / innerW);
    const ms = min + t * (max - min);

    const idx = nearestIndexForMs(ms);
    if (idx === null) return;

    setHoverIndex(idx);
    setHoverClient({ x: e.clientX, y: e.clientY });

    // If dragging, pan the view.
    if (dragRef.current?.isDown && view) {
      const dx = e.clientX - dragRef.current.startClientX;
      const msPerPx =
        (dragRef.current.startMax - dragRef.current.startMin) / innerW;
      const shift = dx * msPerPx;
      const range = dragRef.current.startMax - dragRef.current.startMin;

      let nextMin = dragRef.current.startMin - shift;
      let nextMax = dragRef.current.startMax - shift;

      // Clamp within full data range
      if (nextMin < msMinMax.min) {
        nextMin = msMinMax.min;
        nextMax = msMinMax.min + range;
      }
      if (nextMax > msMinMax.max) {
        nextMax = msMinMax.max;
        nextMin = msMinMax.max - range;
      }

      setView({ min: nextMin, max: nextMax });
    }
  };

  const onMouseLeave: React.MouseEventHandler<SVGSVGElement> = () => {
    setHoverIndex(null);
    setHoverClient(null);
    if (dragRef.current) dragRef.current.isDown = false;
  };

  const onMouseDown: React.MouseEventHandler<SVGSVGElement> = (e) => {
    if (!series.length || !view) return;
    dragRef.current = {
      isDown: true,
      startClientX: e.clientX,
      startMin: view.min,
      startMax: view.max,
    };
  };

  const onMouseUp: React.MouseEventHandler<SVGSVGElement> = () => {
    if (dragRef.current) dragRef.current.isDown = false;
  };

  const onWheel: React.WheelEventHandler<SVGSVGElement> = (e) => {
    if (!svgRef.current || !series.length || !view) return;
    e.preventDefault();

    const rect = svgRef.current.getBoundingClientRect();
    const px = e.clientX - rect.left;
    const svgX = (px / rect.width) * W;

    const { min: fullMin, max: fullMax } = msMinMax;
    if (
      !Number.isFinite(fullMin) ||
      !Number.isFinite(fullMax) ||
      fullMax === fullMin
    )
      return;

    const t = clamp01((svgX - padL) / innerW);
    const anchor = view.min + t * (view.max - view.min);

    // Zoom factor: trackpad wheel down -> zoom out.
    const zoomIn = e.deltaY < 0;
    const factor = zoomIn ? 0.85 : 1.18;
    const range = view.max - view.min;
    const nextRange = clamp(
      range * factor,
      7 * 24 * 60 * 60 * 1000,
      fullMax - fullMin,
    );

    let nextMin = anchor - t * nextRange;
    let nextMax = nextMin + nextRange;

    // Clamp
    if (nextMin < fullMin) {
      nextMin = fullMin;
      nextMax = fullMin + nextRange;
    }
    if (nextMax > fullMax) {
      nextMax = fullMax;
      nextMin = fullMax - nextRange;
    }

    setView({ min: nextMin, max: nextMax });
  };

  const onDoubleClick: React.MouseEventHandler<SVGSVGElement> = () => {
    if (Number.isFinite(msMinMax.min) && Number.isFinite(msMinMax.max)) {
      setView({ min: msMinMax.min, max: msMinMax.max });
    }
  };

  const hoverRow = hoverIndex !== null ? series[hoverIndex] : null;

  // Today vertical line
  const todayMs = parseISODateToMs(todayISO);
  const todayX = xForMs(todayMs);
  const hasData = !!latest;

  // DEV-846: Mini variant now expands INLINE (no modal). See ScheduleThermometer
  // for the source-of-truth comments — Budget mirrors Schedule's structure.
  // isMini = "render collapsed mini layout"; flips to false when expanded so
  // the inline-expanded view shows the full thermometer layout (stats column +
  // bracket + chart) just like the canonical full card.
  const wantsMini = displayMode === "mini";
  const showAsModal = false;
  const isMini = wantsMini && !fullOpen;
  const pctRaw =
    actualLatest && Number.isFinite(Number((actualLatest as any).actual_spend))
      ? Number((actualLatest as any).actual_spend)
      : 0;
  // Finance: allow >100% (actual can exceed budget).
  // For the thermometer fill, always use effectiveScaleFactor so the glass matches the chart.
  const pct100 =
    effectiveScaleFactor > 0 ? Math.max(0, (pctRaw / effectiveScaleFactor) * 100) : 0;

  // --- Full-view side status summary (derived from series) ---
  const prevActualRow =
    actualRows.length >= 2 ? actualRows[actualRows.length - 2] : null;

  const asOfISO = String(
    (actualLatest as any)?.reporting_date ||
      (latest as any)?.reporting_date ||
      "",
  ).trim();

  const asOfMs = isISODate(asOfISO) ? parseISODateToMs(asOfISO) : NaN;

  const baselineAtAsOfPct100 = useMemo(
    () => interpolateBaselineAtDate(baselineRows, asOfMs, (r) => Number(r.baseline_budget), scaleFactor),
    [baselineRows, asOfMs, scaleFactor],
  );

  // Baseline at previous reporting date — needed for overspend-delta arrow direction.
  // Must be a top-level hook (before early returns) to satisfy Rules of Hooks.
  const prevAsOfISO = String((prevActualRow as any)?.reporting_date || "").trim();
  const prevAsOfMs = isISODate(prevAsOfISO) ? parseISODateToMs(prevAsOfISO) : NaN;

  const baselineAtPrevPct100 = useMemo(
    () => interpolateBaselineAtDate(baselineRows, prevAsOfMs, (r) => Number(r.baseline_budget), scaleFactor),
    [baselineRows, prevAsOfMs, scaleFactor],
  );

  if (!projectGUID) {
    const emptyMsg = (
      <div className={styles.scheduleChartEmpty}>
        Select a project to view progress.
      </div>
    );
    return embedded ? (
      emptyMsg
    ) : (
      <div className={styles.scheduleChartWrap}>{emptyMsg}</div>
    );
  }

  const prevPct100 = prevActualRow
    ? scaleFactor > 0
      ? Math.max(0, (Number((prevActualRow as any).actual_spend) / scaleFactor) * 100)
      : 0
    : null;

  // For cost direction: delta is change in OVERSPEND (actual − baseline), not raw actual.
  // Positive deltaOverspendPP → overspend worsened → ▼ (bad).
  // Negative → overspend improved → ▲ (good).
  // Delta in overspend between current and previous period.
  const deltaOverspendPP =
    prevPct100 !== null && baselineAtAsOfPct100 !== null && baselineAtPrevPct100 !== null
      ? (pct100 - baselineAtAsOfPct100) - (prevPct100 - baselineAtPrevPct100)
      : null;

  // Keep raw delta for display text (how much actual % changed).
  const deltaPctPts = prevPct100 === null ? null : pct100 - prevPct100;

  // Variance tolerances in ABSOLUTE PERCENTAGE POINTS.
  // Must match the scoring engine — for cost, overrunPP = actual - baseline (over-budget = bad).
  // FIRST: pull from the KPI instance config (Project Settings → KPI settings).
  // Fallback: global kpiConfig defaults. Final fallback: 10pp/20pp.
  const instanceTols = readVarianceTolerancesFromInstance(
    projectGuidNorm,
    instanceGuidNorm,
    String(title || ""),
    "finance",
  );
  const tolYellowPP = (() => {
    const inst = Number(instanceTols?.yellow_pp);
    if (Number.isFinite(inst) && inst > 0) return inst;
    const glb = Number((kpiConfig as any)?.finance?.yellow_threshold_pp);
    if (Number.isFinite(glb) && glb > 0) return glb;
    return 10;
  })();
  const tolRedPP = (() => {
    const inst = Number(instanceTols?.red_pp);
    if (Number.isFinite(inst) && inst > 0) return inst;
    const glb = Number((kpiConfig as any)?.finance?.red_threshold_pp);
    if (Number.isFinite(glb) && glb > 0) return glb;
    return 20;
  })();

  // behindPP: direct comparison matching the scoring engine (finance.ts).
  // For cost/budget: overrunPP = actual − baseline.  Positive = over-budget = BAD.
  // "decreasing" curves (rare) flip the sign so the same threshold logic applies.
  const behindPP =
    baselineAtAsOfPct100 !== null
      ? direction === "decreasing"
        ? baselineAtAsOfPct100 - pct100
        : pct100 - baselineAtAsOfPct100
      : null;

  // Warning/critical lines in pct100 space (absolute pp offset from baseline).
  // For finance/cost: thresholds are ABOVE baseline (over-budget = bad).
  // No upper cap — actual spend can exceed 100% of budget.
  const warningPct100 =
    baselineAtAsOfPct100 === null
      ? null
      : Math.max(0, baselineAtAsOfPct100 + tolYellowPP);

  const criticalPct100 =
    baselineAtAsOfPct100 === null
      ? null
      : Math.max(0, baselineAtAsOfPct100 + tolRedPP);

  // Variance-colored actual line segments (computed inline, not a hook).
  // Uses the scoring engine's behindPP formula — no clamping of thresholds.
  const varianceSegments: Array<{
    x1: number;
    y1: number;
    x2: number;
    y2: number;
    color: string;
  }> = (() => {
    if (actualRows.length < 2 || !baselineRows.length) return [];

    // Build a baseline lookup by reporting_date
    const baselineMap = new Map<string, number>();
    for (const r of baselineRows) {
      if (
        r.baseline_budget !== null &&
        r.baseline_budget !== undefined &&
        Number.isFinite(Number(r.baseline_budget))
      ) {
        baselineMap.set(r.reporting_date, Number(r.baseline_budget));
      }
    }

    const segments: Array<{
      x1: number;
      y1: number;
      x2: number;
      y2: number;
      color: string;
    }> = [];

    for (let i = 1; i < actualRows.length; i++) {
      const prev = actualRows[i - 1];
      const curr = actualRows[i];

      const prevActual = Number(prev.actual_spend);
      const currActual = Number(curr.actual_spend);
      if (!Number.isFinite(prevActual) || !Number.isFinite(currActual))
        continue;

      const currBaseline = baselineMap.get(curr.reporting_date);

      let segColor = "#2ecc71"; // default: on track

      if (currBaseline !== undefined) {
        // Convert baseline to pct100 space to match scoring engine units
        const blPct100 = scaleFactor === 100 ? currBaseline : currBaseline * 100;
        const actPct100 = scaleFactor === 100 ? currActual : currActual * 100;

        // Cost direction: actual − baseline.  Positive = over-budget = BAD.
        const segBehindPP = direction === "decreasing"
          ? blPct100 - actPct100
          : actPct100 - blPct100;

        if (segBehindPP >= tolRedPP) {
          segColor = "#e74c3c"; // critical
        } else if (segBehindPP >= tolYellowPP) {
          segColor = "#f1c40f"; // warning
        }
      }

      const x1 = xForRow(prev);
      const y1 = yForValue01(clamp01(prevActual / effectiveScaleFactor));
      const x2 = xForRow(curr);
      const y2 = yForValue01(clamp01(currActual / effectiveScaleFactor));

      segments.push({ x1, y1, x2, y2, color: segColor });
    }

    return segments;
  })();

  // Band color for the left-side "glass" summary.
  // Uses the scoring engine's behindPP formula for consistency with KPIGridTable.
  const bandColor = (() => {
    if (behindPP !== null) {
      // behindPP comparison aligned with the scoring engine — no maturity guard
      // needed because subtraction works correctly at any baseline level.
      if (behindPP >= tolRedPP) return "#e74c3c";
      if (behindPP >= tolYellowPP) return "#f1c40f";
      return "#2ecc71";
    }
    // No baseline data at all: legacy thresholds on absolute pct.
    if (pct100 >= 80) return "#2ecc71";
    if (pct100 >= 60) return "#f1c40f";
    return "#e74c3c";
  })();

  if (debugMode) console.debug(`[FinanceThermo] bandColor=${bandColor}, behindPP=${behindPP}, baselineAtAsOfPct100=${baselineAtAsOfPct100}, direction=${direction}, tolYellowPP=${tolYellowPP}, tolRedPP=${tolRedPP}`);

  // Status label for the badge beneath "% Complete"
  const bandLabel =
    bandColor === "#2ecc71"
      ? "Under Budget"
      : bandColor === "#f1c40f"
        ? "Warning"
        : "Over Budget";

  // Arrow glyph: based on overspend TREND (not raw actual change).
  // Positive deltaOverspendPP → overspend worsened → ▼ (bad direction).
  // Negative → overspend improved → ▲ (good direction).
  // Falls back to raw delta if baseline data is incomplete.
  const arrowDelta = deltaOverspendPP ?? deltaPctPts;
  const arrowGlyph =
    arrowDelta === null
      ? "•"
      : arrowDelta > 0.05
        ? "▼"
        : arrowDelta < -0.05
          ? "▲"
          : "•";

  const deltaText =
    deltaPctPts === null
      ? "—"
      : formatDelta(deltaPctPts, 1, "%");

  /*
   * MINI HEADER (DEV-846 — was MINI ROW that opened a modal).
   * Mirrors ScheduleThermometer. Click anywhere on the header toggles
   * fullOpen. No "+" button. When expanded, header gets squared bottom
   * corners + bottom border so it visually attaches to the chart container
   * below. Chart area below excludes itself from the toggle handler.
   */
  const miniHeader = wantsMini ? (
      <div
        onClick={() => projectGUID && setFullOpen((v) => !v)}
        role="button"
        tabIndex={0}
        aria-expanded={fullOpen}
        onKeyDown={(e) => {
          if (!projectGUID) return;
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setFullOpen((v) => !v);
          }
        }}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
          border: "1px solid var(--border-default)",
          borderRadius: fullOpen ? "12px 12px 0 0" : 12,
          borderBottom: fullOpen ? "1px solid var(--border-subtle)" : "1px solid var(--border-default)",
          padding: "10px 12px",
          background: "var(--bg-active)",
          cursor: projectGUID ? "pointer" : "not-allowed",
          opacity: projectGUID ? 1 : 0.6,
          transition: "background 120ms ease",
        }}
      >
        {/* Left group: title */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            minWidth: 0,
          }}
        >
          <div
            style={{
              fontWeight: 700,
              fontSize: 15,
              lineHeight: 1.15,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              minWidth: 0,
            }}
            title={`${String(title || "Progress")}${
              endDateAsOf ? ` (as of ${endDateAsOf})` : ""
            }`}
          >
            {String(title || "Progress")}
            {endDateAsOf ? (
              <span
                style={{
                  fontWeight: 400,
                  fontSize: "0.75em",
                  opacity: 0.7,
                  marginInlineStart: 4,
                }}
              >
                (as of {endDateAsOf})
              </span>
            ) : null}
          </div>
        </div>

        {/* Right group: % → pill */}
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {hasData ? (
            <>
              <span
                style={{
                  fontWeight: 800,
                  fontSize: 15,
                  color: bandColor,
                  fontVariantNumeric: "tabular-nums",
                  whiteSpace: "nowrap",
                  minWidth: 52,
                  textAlign: "right",
                }}
              >
                {formatPercent(pct100, 1)}
              </span>
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minWidth: 72,
                  padding: "3px 8px",
                  borderRadius: 999,
                  fontSize: 9,
                  fontWeight: 700,
                  letterSpacing: 0.3,
                  color: bandColor,
                  textTransform: "uppercase",
                  background: `${bandColor}40`,
                  border: `1px solid ${bandColor}80`,
                  whiteSpace: "nowrap",
                }}
              >
                {bandLabel}
              </span>
            </>
          ) : null}
        </div>
      </div>
    ) : null;

  const fullContentInner = (
    <>
      <div className={styles.scheduleChartHeader}>
        {/* DEV-846: When inline-expanded from a mini header, the mini header
            already shows the title. Suppress the chart's own title to avoid
            duplication; right-side action group still renders. */}
        {wantsMini && fullOpen ? (
          <div />
        ) : (
          <div className={styles.scheduleChartTitle}>
            {String(title || "Progress")}
            {endDateAsOf ? (
              <span
                style={{
                  fontWeight: 400,
                  fontSize: "0.75em",
                  opacity: 0.7,
                  marginInlineStart: 6,
                }}
              >
                (as of {endDateAsOf})
              </span>
            ) : null}
          </div>
        )}
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {/* Budget view mode toggle: % ↔ $ */}
          <button
            type="button"
            onClick={toggleBudgetViewMode}
            title={isAbsoluteView ? t('thermometer.switchToPercent') : t('thermometer.switchToAbsolute')}
            style={{
              padding: "2px 8px",
              borderRadius: 6,
              border: "1px solid var(--border-default)",
              background: "var(--bg-hover)",
              color: "var(--text-primary)",
              fontSize: 11,
              cursor: canShowPercent ? "pointer" : "not-allowed",
              opacity: canShowPercent ? 1 : 0.5,
              whiteSpace: "nowrap",
              userSelect: "none",
            }}
            disabled={!canShowPercent}
          >
            {isAbsoluteView ? currencySymbol : "%"}
          </button>
          {/* Legend toggle */}
          <label
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 5,
              fontSize: 11,
              opacity: 0.75,
              cursor: "pointer",
              userSelect: "none",
              whiteSpace: "nowrap",
            }}
          >
            <input
              type="checkbox"
              checked={showLegend}
              onChange={() => setShowLegend((v) => !v)}
              style={{
                accentColor: "rgba(56, 132, 255, 0.9)",
                cursor: "pointer",
              }}
            />
            Legend
          </label>

          {/* DEV-846: see ScheduleThermometer for the rationale on this condition. */}
          {wantsMini && fullOpen ? (
            <button
              type="button"
              onClick={() => setFullOpen(false)}
              style={{
                padding: "6px 10px",
                borderRadius: 10,
                border: "1px solid var(--border-default)",
                background: "var(--bg-hover)",
                color: "var(--text-primary)",
                fontSize: 12,
                cursor: "pointer",
                whiteSpace: "nowrap",
              }}
              title={t('thermometer.collapse')}
            >
              {t('thermometer.collapse')}
            </button>
          ) : null}

          {/*
            DEV-841 — Two-button affordance (mirrors Schedule).
            ─────────────────────────────────────────────────────────────────
            Database icon : opens the curve / data-points editor (existing
                            "Budget Settings" modal). DATA editing surface.
            Pencil icon   : dispatches `l4e_open_kpi_config` so ProjectDetailPage
                            opens ProjectConfigModal scoped to this Budget
                            instance's edit form. CONFIGURATION surface.

            The Schedule pattern is the source of truth (see ScheduleThermometer
            for the same code with `category: 'schedule'`). Keep both in sync.
          */}
          {canEditCurve ? (
            <>
              <button
                type="button"
                onClick={() => setManageOpen(true)}
                disabled={!projectGUID}
                style={{
                  width: 28,
                  height: 28,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: 6,
                  border: "1px solid var(--border-default)",
                  background: "var(--bg-hover)",
                  color: "var(--text-primary)",
                  cursor: !projectGUID ? "not-allowed" : "pointer",
                  opacity: !projectGUID ? 0.5 : 1,
                  padding: 0,
                }}
                title={t('thermometer.editDataPoints') || 'Edit data points / curve'}
                aria-label={t('thermometer.editDataPoints') || 'Edit data points / curve'}
              >
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <ellipse cx="8" cy="3.25" rx="5.25" ry="1.75" stroke="currentColor" strokeWidth="1.3"/>
                  <path d="M2.75 3.25v9.5c0 .97 2.35 1.75 5.25 1.75s5.25-.78 5.25-1.75v-9.5" stroke="currentColor" strokeWidth="1.3"/>
                  <path d="M2.75 7c0 .97 2.35 1.75 5.25 1.75s5.25-.78 5.25-1.75" stroke="currentColor" strokeWidth="1.3"/>
                  <path d="M2.75 10.5c0 .97 2.35 1.75 5.25 1.75s5.25-.78 5.25-1.75" stroke="currentColor" strokeWidth="1.3"/>
                </svg>
              </button>
              <button
                type="button"
                onClick={() => {
                  // DEV-841: ask the page to open ProjectConfigModal scoped to
                  // this Budget instance. Same event/listener as Schedule;
                  // ProjectDetailPage handles routing.
                  if (!instanceGuidNorm) {
                    window.dispatchEvent(new CustomEvent('l4e_open_kpi_config', {
                      detail: { category: 'finance', projectGuid: projectGuidNorm },
                    }));
                    return;
                  }
                  // DEV-841 / DEV-840: project_guid in payload routes to launcher.
                  window.dispatchEvent(new CustomEvent('l4e_open_kpi_config', {
                    detail: {
                      category: 'finance',
                      projectGuid: projectGuidNorm,
                      instanceGuid: instanceGuidNorm,
                    },
                  }));
                }}
                disabled={!projectGUID}
                style={{
                  width: 28,
                  height: 28,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: 6,
                  border: "1px solid var(--border-default)",
                  background: "var(--bg-hover)",
                  color: "var(--text-primary)",
                  cursor: !projectGUID ? "not-allowed" : "pointer",
                  opacity: !projectGUID ? 0.5 : 1,
                  padding: 0,
                  marginInlineStart: 4,
                }}
                title={t('thermometer.editSettings')}
                aria-label={t('thermometer.editSettings')}
              >
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M11.13 1.87a1.25 1.25 0 0 1 1.77 0l1.23 1.23a1.25 1.25 0 0 1 0 1.77L5.04 13.96l-3.54.5.5-3.54L11.13 1.87Z" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M9.5 3.5l3 3" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
                </svg>
              </button>
            </>
          ) : null}
        </div>
      </div>

      {manageOpen && canEditCurve ? createPortal(
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0, 0, 0, 0.6)",
            zIndex: 100001,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 18,
          }}
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setManageOpen(false);
          }}
        >
          <div
            style={{
              position: "relative",
              width: "min(900px, 96vw)",
              maxHeight: "90vh",
              overflow: "auto",
              borderRadius: 16,
              border: "1px solid var(--border-default)",
              background: "var(--bg-secondary)",
              boxShadow: "0 20px 60px rgba(0,0,0,0.55)",
              padding: 16,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
              }}
            >
              <div style={{ fontSize: 16, fontWeight: 650 }}>
                {t('thermometer.budgetSettings')}
              </div>
              <button
                type="button"
                onClick={() => setManageOpen(false)}
                style={{
                  padding: "4px 10px",
                  borderRadius: 10,
                  border: "1px solid var(--border-default)",
                  background: "var(--bg-hover)",
                  color: "var(--text-tertiary)",
                  cursor: "pointer",
                  fontSize: 18,
                  lineHeight: 1,
                }}
                title={t('thermometer.collapse')}
              >
                ×
              </button>
            </div>

            {/* ═══ Sectioned modal layout (DEV-787) ═══ */}
            <div style={{ marginTop: 12 }}>

            {/* ── Section 1: Curve Setup (all config + generation actions) ── */}
            <CollapsibleSection title={t('modalSection.curveSetup')} defaultOpen={!openedWithCurveData}>
              <div style={{ display: "grid", gridTemplateColumns: "180px 1fr", gap: 10, alignItems: "center" }}>
              <div style={{ fontSize: 12, opacity: 0.85 }}>
                Generation method
              </div>
              <select
                disabled={
                  readOnly || !canManageBaseline || generationControlsLocked
                }
                value={curveType}
                onChange={(e) => setCurveType(e.target.value as any)}
                style={{
                  padding: "8px 10px",
                  borderRadius: 10,
                  border: "1px solid var(--border-default)",
                  background: "var(--bg-hover)",
                  color: "var(--text-primary)",
                }}
              >
                <option value="linear">{t('curveConfig.linear')}</option>
                <option value="front_loaded">{t('curveConfig.frontLoaded')}</option>
                <option value="back_loaded">{t('curveConfig.backLoaded')}</option>
                <option value="sigmoidal">{t('curveConfig.sigmoidal')}</option>
                <option value="copy">Copy another project</option>
                <option value="manual">{t('curveConfig.manual')}</option>
                <option value="custom" disabled>
                  Custom (coming soon)
                </option>
              </select>
              {curveType === "copy" ? (
                <>
                  <div style={{ fontSize: 12, opacity: 0.85 }}>
                    Copy from project
                  </div>
                  <select
                    disabled={
                      readOnly || !canManageBaseline || generationControlsLocked
                    }
                    value={copyFromGuid}
                    onChange={(e) => setCopyFromGuid(e.target.value)}
                    style={{
                      padding: "8px 10px",
                      borderRadius: 10,
                      border: "1px solid var(--border-default)",
                      background: "var(--bg-hover)",
                      color: "var(--text-primary)",
                    }}
                  >
                    {copyCandidates.length ? (
                      copyCandidates.map((p: any) => {
                        const g = String(
                          p?.project_GUID || p?.project_guid || "",
                        );
                        const name = String(p?.project_name || g);
                        return (
                          <option key={g} value={g}>
                            {name}
                          </option>
                        );
                      })
                    ) : (
                      <option value="">
                        (No other projects found for this owner/community)
                      </option>
                    )}
                  </select>
                </>
              ) : null}
              </div>

              {/* ── Scope ── */}
              <div style={{ display: "grid", gridTemplateColumns: "180px 1fr", gap: 10, alignItems: "center", marginTop: 12 }}>
              {/* ── WBS Scope (Professional tier only) ── */}
              {ganttTasks && ganttTasks.length > 0 && (() => {
                const wbsSet = new Map<string, string>();
                const sorted = [...ganttTasks].sort((a, b) => a.wbs.localeCompare(b.wbs, undefined, { numeric: true }));
                for (const t of sorted) {
                  if (!t.wbs) continue;
                  {
                    const indent = (t.wbs.match(/\./g) || []).length;
                    const prefix = "  ".repeat(indent);
                    wbsSet.set(t.wbs, `${prefix}${t.wbs} — ${t.taskName}`);
                  }
                }
                return (
                  <>
                    <div style={{ fontSize: 12, opacity: 0.85 }}>
                      {t('wbsPicker.label')}
                    </div>
                    <select
                      disabled={readOnly || !canManageBaseline}
                      value={wbsCode}
                      onChange={(e) => setWbsCode(e.target.value)}
                      style={{
                        padding: "8px 10px",
                        borderRadius: 10,
                        border: "1px solid var(--border-default)",
                        background: "var(--bg-hover)",
                        color: "var(--text-primary)",
                      }}
                    >
                      <option value="">{t('wbsPicker.allProject')}</option>
                      {[...wbsSet.entries()].map(([code, label]) => (
                        <option key={code} value={code}>{label}</option>
                      ))}
                    </select>
                  </>
                );
              })()}

              {/* Direction dropdown removed — hardcoded to "increasing" (DEV-787) */}

              {/* ── Custom curve window ── */}
              <div style={{ fontSize: 12, opacity: 0.85 }}>
                Custom curve window
              </div>
              {canManageBaseline ? (
                <div
                  style={{
                    display: "flex",
                    gap: 10,
                    flexWrap: "wrap",
                    alignItems: "center",
                  }}
                >
                  <div
                    style={{
                      fontSize: 12,
                      opacity: 0.85,
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    From
                  </div>
                  <div
                    style={{ display: "flex", flexDirection: "column", gap: 2 }}
                  >
                    <input
                      disabled={readOnly || !canManageBaseline}
                      type="text"
                      value={formatDateUS(overrideStartISO || "")}
                      onChange={(e) => {
                        overrideTouchedRef.current = true;
                        const us = String(e.target.value || "").trim();
                        if (!us) {
                          setOverrideStartISO("");
                          return;
                        }
                        const parsedISO = usToISO(us);
                        if (!parsedISO) return;
                        let next = parsedISO;
                        const maxISO = overrideEndISO
                          ? addDaysISO(overrideEndISO, -1)
                          : endISO
                            ? addDaysISO(endISO, -1)
                            : undefined;
                        if (maxISO && next > maxISO) next = maxISO;
                        setOverrideStartISO(next);
                      }}
                      style={{
                        padding: "8px 10px",
                        borderRadius: 10,
                        border: "1px solid var(--border-default)",
                        background: "var(--bg-hover)",
                        color: "var(--text-primary)",
                        width: 120,
                      }}
                    />
                    {overrideStartISO &&
                    startISO &&
                    overrideStartISO !== startISO ? (
                      <div style={{ fontSize: 10, opacity: 0.45 }}>
                        project: {formatDateUS(startISO)}
                      </div>
                    ) : null}
                  </div>
                  <div
                    style={{
                      fontSize: 12,
                      opacity: 0.85,
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    Until
                  </div>
                  <div
                    style={{ display: "flex", flexDirection: "column", gap: 2 }}
                  >
                    <input
                      disabled={readOnly || !canManageBaseline}
                      type="text"
                      value={formatDateUS(overrideEndISO || "")}
                      onChange={(e) => {
                        overrideTouchedRef.current = true;
                        const us = String(e.target.value || "").trim();
                        if (!us) {
                          setOverrideEndISO("");
                          setUntilISO("");
                          return;
                        }
                        const parsedISO = usToISO(us);
                        if (!parsedISO) return;
                        let next = parsedISO;
                        const minISO = overrideStartISO
                          ? addDaysISO(overrideStartISO, 1)
                          : startISO
                            ? addDaysISO(startISO, 1)
                            : undefined;
                        if (minISO && next < minISO) next = minISO;
                        setOverrideEndISO(next);
                        setUntilISO(next);
                      }}
                      style={{
                        padding: "8px 10px",
                        borderRadius: 10,
                        border: "1px solid var(--border-default)",
                        background: "var(--bg-hover)",
                        color: "var(--text-primary)",
                        width: 120,
                      }}
                    />
                    {overrideEndISO && endISO && overrideEndISO !== endISO ? (
                      <div style={{ fontSize: 10, opacity: 0.45 }}>
                        project: {formatDateUS(endISO)}
                      </div>
                    ) : null}
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      overrideTouchedRef.current = true;
                      setOverrideStartISO(startISO || "");
                      setOverrideEndISO(endISO || "");
                      setUntilISO(endISO || "");
                    }}
                    style={{
                      padding: "6px 10px",
                      borderRadius: 10,
                      border: "1px solid var(--border-default)",
                      background: "var(--bg-hover)",
                      color: "var(--text-tertiary)",
                      cursor: "pointer",
                      fontSize: 11,
                    }}
                  >
                    Reset
                  </button>
                </div>
              ) : (
                <div
                  style={{
                    display: "flex",
                    gap: 12,
                    flexWrap: "wrap",
                    fontSize: 12,
                  }}
                >
                  <div style={{ opacity: 0.9 }}>
                    From: <b>{startISO ? formatDateUS(startISO) : "(not set)"}</b>
                  </div>
                  <div style={{ opacity: 0.9 }}>
                    Until: <b>{endISO ? formatDateUS(endISO) : "(not set)"}</b>
                  </div>
                </div>
              )}
              </div>

              {/* ── Reporting Schedule + Assignment ── */}
              <div style={{ display: "grid", gridTemplateColumns: "180px 1fr", gap: 10, alignItems: "center", marginTop: 12 }}>

              {/* ── Assigned role ── */}
              <div style={{ fontSize: 12, opacity: 0.85 }}>{t('roleAssignment.label')}</div>
              <select
                disabled={readOnly || !canManageBaseline}
                value={assignedRoleSlotGuid}
                onChange={(e) => setAssignedRoleSlotGuid(e.target.value)}
                style={{
                  padding: "8px 10px",
                  borderRadius: 10,
                  border: "1px solid var(--border-default)",
                  background: "var(--bg-hover)",
                  color: "var(--text-primary)",
                }}
              >
                <option value="">{t('roleAssignment.projectOwnerDefault')}</option>
                {projectRoleSlots.map((slot) => (
                  <option key={slot.roleSlotGuid} value={slot.roleSlotGuid}>
                    {slot.roleLabel}{slot.isOwnerDefault ? ` (${t('roleAssignment.ownerDefault')})` : ""}
                  </option>
                ))}
              </select>

              {/* ── Reporting cadence ── */}
              <div style={{ fontSize: 12, opacity: 0.85 }}>Report every</div>
              <div
                style={{
                  display: "flex",
                  gap: 10,
                  flexWrap: "nowrap",
                  alignItems: "center",
                }}
              >
                <input
                  disabled={
                    readOnly || !canManageBaseline || generationControlsLocked
                  }
                  type="number"
                  min={1}
                  value={repeatEvery}
                  onChange={(e) =>
                    setRepeatEvery(Math.max(1, Number(e.target.value || 1)))
                  }
                  style={{
                    width: 84,
                    padding: "8px 10px",
                    borderRadius: 10,
                    border: "1px solid var(--border-default)",
                    background: "var(--bg-hover)",
                    color: "var(--text-primary)",
                  }}
                />

                <select
                  disabled={
                    readOnly || !canManageBaseline || generationControlsLocked
                  }
                  value={reportingCadence}
                  onChange={(e) => setReportingCadence(e.target.value as any)}
                  style={{
                    padding: "8px 10px",
                    borderRadius: 10,
                    border: "1px solid var(--border-default)",
                    background: "var(--bg-hover)",
                    color: "var(--text-primary)",
                  }}
                >
                  <option value="daily">day</option>
                  <option value="weekly">week</option>
                  <option value="monthly">month</option>
                  <option value="quarterly">quarter</option>
                </select>

                {reportingCadence === "weekly" ? (
                  <div
                    style={{
                      display: "flex",
                      gap: 6,
                      alignItems: "center",
                      flexShrink: 0,
                    }}
                  >
                    {(
                      [
                        { i: 1, label: "M" },
                        { i: 2, label: "T" },
                        { i: 3, label: "W" },
                        { i: 4, label: "T" },
                        { i: 5, label: "F" },
                        { i: 6, label: "S" },
                        { i: 0, label: "S" },
                      ] as any[]
                    ).map((d) => (
                      <button
                        key={String(d.i) + d.label}
                        type="button"
                        onClick={() => {
                          const next = [...weekdays];
                          next[d.i] = !next[d.i];
                          setWeekdays(next);
                        }}
                        style={{
                          width: 30,
                          height: 30,
                          borderRadius: 99,
                          border: weekdays[d.i]
                            ? "1px solid rgba(56,132,255,0.5)"
                            : "1px solid rgba(200,200,200,0.4)",
                          background: weekdays[d.i]
                            ? "rgba(56, 132, 255, 0.35)"
                            : "var(--text-primary)",
                          color: weekdays[d.i] ? "var(--text-primary)" : "#222",
                          cursor: "pointer",
                          fontWeight: 650,
                          fontSize: 12,
                        }}
                      >
                        {d.label}
                      </button>
                    ))}
                    {showWeekdayWarning ? (
                      <div
                        style={{ fontSize: 12, opacity: 0.85, marginInlineStart: 8 }}
                      >
                        <span style={{ color: "var(--color-warning)" }}>⚠</span> Select
                        day(s)
                      </div>
                    ) : null}
                  </div>
                ) : null}

                {reportingCadence === "monthly" ? (
                  <div
                    style={{
                      display: "flex",
                      gap: 8,
                      alignItems: "center",
                      flexShrink: 0,
                    }}
                  >
                    <div style={{ fontSize: 12, opacity: 0.9, flexShrink: 0 }}>
                      on
                    </div>
                    <select
                      disabled={
                        readOnly ||
                        !canManageBaseline ||
                        generationControlsLocked
                      }
                      value={monthlyMode}
                      onChange={(e) => setMonthlyMode(e.target.value as any)}
                      style={{
                        padding: "8px 10px",
                        borderRadius: 10,
                        border: "1px solid var(--border-default)",
                        background: "var(--bg-hover)",
                        color: "var(--text-primary)",
                        flexShrink: 0,
                      }}
                    >
                      <option value="day">day #</option>
                      <option value="first">first</option>
                      <option value="last">last</option>
                    </select>

                    {monthlyMode === "day" ? (
                      <input
                        disabled={
                          readOnly ||
                          !canManageBaseline ||
                          generationControlsLocked
                        }
                        type="number"
                        min={1}
                        max={31}
                        value={monthlyDayOfMonth}
                        onChange={(e) =>
                          setMonthlyDayOfMonth(
                            Math.max(
                              1,
                              Math.min(31, Number(e.target.value || 1)),
                            ),
                          )
                        }
                        style={{
                          padding: "8px 10px",
                          borderRadius: 10,
                          border: "1px solid var(--border-default)",
                          background: "var(--bg-hover)",
                          color: "var(--text-primary)",
                          width: 90,
                        }}
                      />
                    ) : (
                      <select
                        disabled={
                          readOnly ||
                          !canManageBaseline ||
                          generationControlsLocked
                        }
                        value={monthlyWeekday}
                        onChange={(e) =>
                          setMonthlyWeekday(Number(e.target.value))
                        }
                        style={{
                          padding: "8px 10px",
                          borderRadius: 10,
                          border: "1px solid var(--border-default)",
                          background: "var(--bg-hover)",
                          color: "var(--text-primary)",
                        }}
                      >
                        <option value={0}>Sunday</option>
                        <option value={1}>Monday</option>
                        <option value={2}>Tuesday</option>
                        <option value={3}>Wednesday</option>
                        <option value={4}>Thursday</option>
                        <option value={5}>Friday</option>
                        <option value={6}>Saturday</option>
                      </select>
                    )}

                    <InfoIcon title="Monthly cadence uses a rule (day #, first weekday, or last weekday)." />
                  </div>
                ) : null}
              </div>
              </div>

              {/* ── Setup action buttons ── */}
              {canManageBaseline ? (
                <div style={{ gridColumn: "1 / -1", display: "flex", gap: 10, flexWrap: "wrap", marginTop: 14, paddingTop: 10, borderTop: "1px solid var(--border-subtle)" }}>
                  <button
                    type="button"
                    onClick={handleInitializeDates}
                    disabled={readOnly || generationControlsLocked}
                    style={{
                      padding: "8px 12px", borderRadius: 10,
                      border: "1px solid var(--border-default)",
                      background: "var(--bg-hover)", color: "var(--text-primary)",
                      cursor: "pointer", opacity: generationControlsLocked ? 0.6 : 1,
                    }}
                  >
                    Initialize dates
                  </button>
                  <button
                    type="button"
                    onClick={handleGenerateBaseline}
                    disabled={readOnly || generationControlsLocked || !startISO || !endISO}
                    style={{
                      padding: "8px 12px", borderRadius: 10,
                      border: "1px solid var(--border-default)",
                      background: "var(--accent-primary)", color: "var(--text-primary)",
                      cursor: "pointer", opacity: generationControlsLocked ? 0.6 : 1,
                    }}
                  >
                    Generate baseline
                  </button>
                  {canEditCurve ? (
                    <>
                      <div style={{ fontSize: 12, opacity: 0.55, marginInlineStart: 6, display: "flex", alignItems: "center" }}>Populate actuals:</div>
                      <button
                        type="button"
                        onClick={backfillActualsFromBaseline}
                        style={{
                          padding: "6px 12px", borderRadius: 10,
                          border: "1px solid rgba(46,204,113,0.3)",
                          background: "rgba(34, 197, 94, 0.12)",
                          color: "var(--text-primary)", cursor: "pointer", fontSize: 12,
                        }}
                        title="Copy baseline values into actuals for all dates up to today"
                      >
                        Backfill from baseline
                      </button>
                      <button
                        type="button"
                        onClick={simulateActualsWithVariance}
                        style={{
                          padding: "6px 12px", borderRadius: 10,
                          border: "1px solid rgba(255,165,0,0.3)",
                          background: "rgba(245, 158, 11, 0.12)",
                          color: "var(--text-primary)", cursor: "pointer", fontSize: 12,
                        }}
                        title="Fill all actuals with simulated values that vary from baseline"
                      >
                        Simulate with variance
                      </button>
                    </>
                  ) : null}
                </div>
              ) : null}
            </CollapsibleSection>

            {/* ── Section 2: Data (grid + Clear/Lock/Apply) ── */}
            <CollapsibleSection title={t('modalSection.data')} defaultOpen={true}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 10, alignItems: "center" }}>
              {/* Grid paste hint is built into ExcelPasteGrid placeholder */}
              <div
                style={{
                  gridColumn: "1 / -1",
                  marginTop: 8,
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <div style={{ width: "100%", maxWidth: 900 }}>
                  <ExcelPasteGrid
                    value={gridValue}
                    onChange={(v) => {
                      if (readOnly) return;
                      if (canManageBaseline) {
                        setGridValue(v);
                        return;
                      }

                      // Content editors: Actuals-only edits.
                      if (!canEditActualsOnly) return;

                      const prev = gridValue?.cells || [];
                      const nextIn = v?.cells || [];
                      if (!prev.length) return;

                      // Preserve the entire grid except column 3 (Actual).
                      const next = prev.map((row) => [...row]);
                      const maxR = Math.min(next.length, nextIn.length);
                      for (let r = 1; r < maxR; r++) {
                        const val = String(nextIn?.[r]?.[3] ?? "");
                        next[r][3] = val;
                      }
                      setGridValue({ cells: next });
                    }}
                    minRows={8}
                    minCols={4}
                    lockedRows={1}
                    showAddRowCol={false}
                    showClear={false}
                    highlightRows={currentReportingHighlight}
                  />
                </div>
              </div>

              <div
                style={{
                  gridColumn: "1 / -1",
                  display: "flex",
                  justifyContent: "space-between",
                  gap: 10,
                  marginTop: 12,
                  flexWrap: "wrap",
                }}
              >
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                  {canManageBaseline ? (
                    <button
                      type="button"
                      onClick={clearBaseline}
                      disabled={readOnly}
                      style={{
                        padding: "8px 12px",
                        borderRadius: 10,
                        border: clearFlash
                          ? "1px solid rgba(46, 204, 113, 0.35)"
                          : "1px solid rgba(231,76,60,0.25)",
                        background: clearFlash
                          ? "rgba(34, 197, 94, 0.18)"
                          : "rgba(239, 68, 68, 0.06)",
                        color: clearFlash
                          ? "rgba(34, 197, 94, 0.9)"
                          : "var(--text-secondary)",
                        cursor: "pointer",
                      }}
                    >
                      Clear Grid
                    </button>
                  ) : null}
                  {/* Lock/Unlock Baseline (audit trail) */}
                  {auditEnabled && canManageBaseline ? (
                    <button
                      type="button"
                      title={baselineLocked
                        ? `${t('thermometer.baselineLockedAt', { date: baselineLockedAt ? new Date(baselineLockedAt).toLocaleDateString() : '?' })}`
                        : t('thermometer.lockBaseline')}
                      onClick={() => {
                        const action = baselineLocked ? 'unlock' : 'lock';
                        const msg = action === 'lock'
                          ? t('thermometer.confirmLock')
                          : t('thermometer.confirmUnlock');
                        showConfirm(msg, () => {
                          const nowISO = new Date().toISOString();
                          const nextLocked = action === 'lock';
                          const kpiCfg = getProjectKpiConfigOverride(projectGuidNorm as any);
                          if (kpiCfg?.category_instances) {
                            const insts = (kpiCfg.category_instances as any).finance || [];
                            for (const inst of insts) {
                              if (String(inst.instance_guid || "").toUpperCase() === instanceGuidNorm.toUpperCase()) {
                                inst.baseline_locked = nextLocked;
                                inst.baseline_locked_at = nextLocked ? nowISO : undefined;
                              }
                            }
                            upsertProjectKpiConfigOverride(kpiCfg);
                          }
                          setBaselineLocked(nextLocked);
                          setBaselineLockedAt(nextLocked ? nowISO : undefined);
                          pushStatus("info", nextLocked
                            ? `Baseline locked at ${nowISO}`
                            : `Baseline unlocked at ${nowISO}`);
                        });
                      }}
                      style={{
                        padding: "8px 12px",
                        borderRadius: 10,
                        border: baselineLocked
                          ? "1px solid rgba(231,76,60,0.25)"
                          : "1px solid rgba(56,132,255,0.25)",
                        background: baselineLocked
                          ? "rgba(239, 68, 68, 0.06)"
                          : "rgba(56, 132, 255, 0.06)",
                        color: "var(--text-secondary)",
                        cursor: "pointer",
                        fontSize: 13,
                      }}
                    >
                      {baselineLocked ? `🔒 ${t('thermometer.unlockBaseline')}` : `🔓 ${t('thermometer.lockBaseline')}`}
                    </button>
                  ) : null}
                </div>

                <div style={{ display: "flex", gap: 10 }}>
                  <button
                    type="button"
                    onClick={applyGridBaseline}
                    style={{
                      padding: "8px 16px",
                      borderRadius: 10,
                      border: "1px solid rgba(56,132,255,0.4)",
                      background: "var(--accent-primary)",
                      color: "var(--text-primary)",
                      cursor: "pointer",
                      fontWeight: 600,
                    }}
                  >
                    Apply
                  </button>
                </div>
              </div>

              {/* Populate Actuals moved to Curve Setup section */}
              </div>
            </CollapsibleSection>

            </div>{/* end sectioned modal layout */}

            {/* ── Inline alert / confirm overlay ── */}
            {modalAlert ? (
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  background: "var(--bg-elevated)",
                  borderRadius: 16,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  zIndex: 10,
                }}
                onMouseDown={(e) => {
                  if (e.target === e.currentTarget) setModalAlert(null);
                }}
              >
                <div
                  style={{
                    background: "var(--bg-secondary)",
                    border: "1px solid var(--border-default)",
                    borderRadius: 14,
                    padding: "20px 24px",
                    maxWidth: 420,
                    width: "90%",
                    boxShadow: "0 12px 40px rgba(0,0,0,0.5)",
                  }}
                >
                  <div
                    style={{
                      fontSize: 13,
                      lineHeight: 1.55,
                      color: "var(--text-primary)",
                      marginBottom: 16,
                    }}
                  >
                    {modalAlert.message}
                  </div>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "flex-end",
                      gap: 8,
                    }}
                  >
                    {modalAlert.type === "confirm" ? (
                      <>
                        <button
                          type="button"
                          onClick={() => setModalAlert(null)}
                          style={{
                            padding: "7px 14px",
                            borderRadius: 8,
                            border: "1px solid var(--border-default)",
                            background: "var(--bg-hover)",
                            color: "var(--text-tertiary)",
                            cursor: "pointer",
                            fontSize: 12,
                          }}
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            modalAlert.onConfirm?.();
                            setModalAlert(null);
                          }}
                          style={{
                            padding: "7px 14px",
                            borderRadius: 8,
                            border: "1px solid rgba(231,76,60,0.35)",
                            background: "rgba(239, 68, 68, 0.2)",
                            color: "var(--text-primary)",
                            cursor: "pointer",
                            fontSize: 12,
                            fontWeight: 600,
                          }}
                        >
                          Confirm
                        </button>
                      </>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setModalAlert(null)}
                        style={{
                          padding: "7px 14px",
                          borderRadius: 8,
                          border: "1px solid rgba(56,132,255,0.3)",
                          background: "rgba(56,132,255,0.2)",
                          color: "var(--text-primary)",
                          cursor: "pointer",
                          fontSize: 12,
                          fontWeight: 600,
                        }}
                      >
                        OK
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      , document.body) : null}

      <div className={styles.scheduleChartBody}>
        {!isMini ? (
          <div className={styles.scheduleSidePanel}>
            <div
              className={styles.scheduleSideArrow}
              style={{ color: bandColor }}
              aria-hidden
            >
              {arrowGlyph}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <div
                className={styles.scheduleSideValue}
                style={{ color: bandColor }}
              >
                {formatPercent(pct100, 1)}
              </div>
              {/* KPI shape indicator (colored dot or trend arrow) */}
              <svg
                width="28"
                height="28"
                viewBox="0 0 28 28"
                style={{ flexShrink: 0, marginTop: 2 }}
                aria-label={bandLabel}
              >
                {arrowGlyph === "▲" ? (
                  <polygon points="14,2 26,26 2,26" fill={bandColor} />
                ) : arrowGlyph === "▼" ? (
                  <polygon points="14,26 26,2 2,2" fill={bandColor} />
                ) : (
                  <circle cx="14" cy="14" r="11" fill={bandColor} />
                )}
              </svg>
            </div>
            <div className={styles.scheduleSideUnits}>Actual Cost</div>

            {/* Status badge — translucent pill */}
            <div
              className={styles.scheduleSideBadge}
              style={{
                background: `${bandColor}40`,
                color: bandColor,
                borderColor: `${bandColor}80`,
              }}
            >
              {bandLabel}
            </div>

            {/* Vertical progress bar (thermometer) */}
            {(() => {
              // Dynamic scale for the side bar — allows values >100%.
              const barMax = Math.max(
                100,
                pct100,
                Number(warningPct100) || 0,
                Number(criticalPct100) || 0,
                Number(baselineAtAsOfPct100) || 0,
              );
              const barPct = (v: number) => clamp((v / barMax) * 100, 0, 100);
              return (
            <div
              className={styles.scheduleSideBarWrap}
              aria-hidden
              style={{
                ["--plotPadTop" as any]: `${plotPadPx.top}px`,
                ["--plotPadBottom" as any]: `${plotPadPx.bottom}px`,
              }}
            >
              <div className={styles.scheduleSideBar}>
                {/* Fill (actual %) */}
                <div
                  className={styles.scheduleSideBarFill}
                  style={{
                    height: `${barPct(pct100)}%`,
                    background: bandColor,
                  }}
                />

                {/* Thresholds + baseline markers (all scaled to dynamic max) */}
                {Number.isFinite(Number(warningPct100)) && (
                  <div
                    className={styles.scheduleSideBarWarn}
                    style={{
                      bottom: `${barPct(Number(warningPct100))}%`,
                    }}
                  />
                )}
                {Number.isFinite(Number(criticalPct100)) && (
                  <div
                    className={styles.scheduleSideBarCrit}
                    style={{
                      bottom: `${barPct(Number(criticalPct100))}%`,
                    }}
                  />
                )}
                {Number.isFinite(Number(baselineAtAsOfPct100)) && (
                  <div
                    className={styles.scheduleSideBarBaseline}
                    style={{
                      bottom: `${barPct(Number(baselineAtAsOfPct100))}%`,
                    }}
                  />
                )}

                {/* Glass highlight — runs from bottom of bar up to baseline marker */}
                {Number.isFinite(Number(baselineAtAsOfPct100)) && (
                  <div
                    className={styles.scheduleSideBarGloss}
                    style={{
                      top: `${clamp(100 - barPct(Number(baselineAtAsOfPct100)), 0, 100)}%`,
                    }}
                  />
                )}
              </div>
            </div>
              );
            })()}
            <div className={styles.scheduleSideLower}>
              <div className={styles.scheduleSideSub}>
                <span className={styles.scheduleSideLabel}>Budget</span>
                <span className={styles.scheduleSideSubValue}>
                  {baselineAtAsOfPct100 === null
                    ? "—"
                    : formatPercent(baselineAtAsOfPct100, 1)}
                </span>
              </div>
              <div className={styles.scheduleSideSub}>
                <span className={styles.scheduleSideLabel}>Warning</span>
                <span
                  className={styles.scheduleSideSubValue}
                  style={{ color: "var(--color-warning)", fontWeight: 700 }}
                >
                  {warningPct100 !== null
                    ? formatPercent(warningPct100, 1)
                    : `±${tolYellowPP} pp`}
                </span>
              </div>
              <div className={styles.scheduleSideSub}>
                <span className={styles.scheduleSideLabel}>Critical</span>
                <span
                  className={styles.scheduleSideSubValue}
                  style={{ color: "var(--color-error)", fontWeight: 700 }}
                >
                  {criticalPct100 !== null
                    ? formatPercent(criticalPct100, 1)
                    : `±${tolRedPP} pp`}
                </span>
              </div>

              <div className={styles.scheduleSideDivider} />

              <div className={styles.scheduleSideSub}>
                <span className={styles.scheduleSideLabel}>Previous</span>
                <span className={styles.scheduleSideSubValue}>
                  {prevPct100 === null
                    ? "—"
                    : formatPercent(prevPct100, 1)}
                </span>
              </div>
              <div className={styles.scheduleSideSub}>
                <span className={styles.scheduleSideLabel}>Δ Change</span>
                <span className={styles.scheduleSideSubValue}>{deltaText}</span>
              </div>
            </div>
          </div>
        ) : null}

        <div
          ref={chartPanelRef}
          className={styles.scheduleChartPanel}
          style={isMini ? { width: "100%" } : undefined}
        >
          {!series || series.length === 0 ? (
            !isFinanceHydrated() && projectGUID ? (
              /* ── Skeleton loader while API data is hydrating ── */
              <div className={styles.chartSkeletonWrap}>
                <div className={styles.chartSkeletonHeader}>
                  <div className={styles.chartSkeletonTitle} />
                  <div className={styles.chartSkeletonBadge} />
                </div>
                <div className={styles.chartSkeletonBody}>
                  {[38, 52, 44, 67, 58, 72, 60, 80, 74, 85, 78, 90].map(
                    (h, i) => (
                      <div
                        key={i}
                        className={styles.chartSkeletonBar}
                        style={{
                          height: `${h}%`,
                          animationDelay: `${i * 0.08}s`,
                        }}
                      />
                    ),
                  )}
                </div>
                <div className={styles.chartSkeletonAxis} />
                <div className={styles.chartSkeletonLabel}>
                  {[0, 1, 2, 3, 4].map((i) => (
                    <div key={i} className={styles.chartSkeletonLabelDot} />
                  ))}
                </div>
                <div className={styles.chartSkeletonStatus}>
                  Loading chart data…
                </div>
              </div>
            ) : (
              <div
                style={{
                  border: "1px dashed var(--border-strong)",
                  borderRadius: 14,
                  padding: 14,
                  marginBottom: 10,
                  background: "var(--bg-hover)",
                  color: "var(--text-primary)",
                  fontSize: 13,
                  lineHeight: 1.35,
                }}
              >
                <div style={{ fontWeight: 650, marginBottom: 6 }}>
                  No budget curve exists yet for this project.
                </div>
                <div style={{ opacity: 0.85 }}>
                  Initialize reporting dates to begin entering{" "}
                  <b>Actual Cost</b> values. Budget baseline generation is
                  restricted to Admin/Owner.
                </div>
                {!startISO || !endISO ? (
                  <div style={{ marginTop: 8, opacity: 0.9 }}>
                    ⚠ Please set <b>Project Start Date</b> and <b>End Date</b>{" "}
                    in <b>Project Settings → Basics</b>.
                  </div>
                ) : null}
                {canEditCurve ? (
                  <div
                    style={{
                      marginTop: 10,
                      display: "flex",
                      gap: 10,
                      flexWrap: "wrap",
                    }}
                  >
                    <button
                      type="button"
                      onClick={() => setManageOpen(true)}
                      style={{
                        padding: "8px 12px",
                        borderRadius: 10,
                        border: "1px solid var(--border-default)",
                        background: "var(--bg-hover)",
                        color: "var(--text-primary)",
                        cursor: "pointer",
                      }}
                    >
                      Initialize / manage budget curve
                    </button>
                  </div>
                ) : null}
              </div>
            )
          ) : null}

          {/* DEV-846: Was {miniRow ? miniRow : (...)} — see ScheduleThermometer for the
              full rationale. Mini variant header is rendered separately above; this
              section always renders the chart now. */}
          <>
              <svg
                ref={svgRef}
                className={styles.scheduleChartSvg}
                viewBox={`0 0 ${W} ${H}`}
                preserveAspectRatio="none"
                role="img"
                aria-label="Budget progress chart showing baseline budget, actual cost, and forecast spend over reporting dates"
                onMouseMove={onMouseMove}
                onMouseLeave={onMouseLeave}
                onMouseDown={onMouseDown}
                onMouseUp={onMouseUp}
                onWheel={onWheel}
                onDoubleClick={onDoubleClick}
              >
                {/* Gridlines */}
                {yTicks.map((t) => {
                  const y = yForValue01(t.v01);
                  return (
                    <line
                      key={t.label}
                      x1={padL}
                      y1={y}
                      x2={W - padR}
                      y2={y}
                      className={styles.scheduleChartGrid}
                    />
                  );
                })}

                {/* Axes */}
                <line
                  x1={padL}
                  y1={padT}
                  x2={padL}
                  y2={H - padB}
                  className={styles.scheduleChartAxis}
                />
                <line
                  x1={padL}
                  y1={H - padB}
                  x2={W - padR}
                  y2={H - padB}
                  className={styles.scheduleChartAxis}
                />

                {/* Today marker — colored to match KPI band color */}
                {Number.isFinite(todayX) ? (
                  <line
                    x1={todayX}
                    y1={padT}
                    x2={todayX}
                    y2={H - padB}
                    stroke={bandColor}
                    strokeWidth={1.5}
                    strokeDasharray="6,4"
                    opacity={0.7}
                  />
                ) : null}

                {/* Warning/Critical threshold lines are rendered dynamically on hover (see hover block below) */}

                {/* Y labels */}
                {yTicks.map((t) => {
                  const y = yForValue01(t.v01);
                  return (
                    <text
                      key={t.label}
                      x={padL - 10}
                      y={y + 4}
                      textAnchor="end"
                      className={styles.scheduleChartTickText}
                    >
                      {t.label}
                    </text>
                  );
                })}

                {/* X labels — only visible when legend is shown */}
                {showLegend && (
                  <>
                    <text
                      x={padL}
                      y={H - 10}
                      textAnchor="start"
                      className={styles.scheduleChartTickText}
                    >
                      {xTickLabels.left}
                    </text>
                    {chartPanelWidth > 350 && (
                      <text
                        x={padL + innerW / 2}
                        y={H - 10}
                        textAnchor="middle"
                        className={styles.scheduleChartTickText}
                      >
                        {xTickLabels.mid}
                      </text>
                    )}
                    <text
                      x={padL + innerW}
                      y={H - 10}
                      textAnchor="end"
                      className={styles.scheduleChartTickText}
                    >
                      {xTickLabels.right}
                    </text>
                  </>
                )}

                {/* ── Choreographed entrance: baseline → area → actual → dots → forecast ── */}

                {/* 1. Baseline draws on first */}
                {baselinePath ? (
                  <path
                    key={`baseline-${projectGUID}-${viewMinMax.min}-${viewMinMax.max}`}
                    d={baselinePath}
                    className={styles.scheduleChartLineBaseline}
                    pathLength={1}
                  />
                ) : null}

                {/* 2. Area fill under actual line wipes left-to-right */}
                {actualPath ? (() => {
                  const baseY = yForValue01(0);
                  const firstX = actualRows.length ? xForRow(actualRows[0]) : 0;
                  const lastX = actualRows.length ? xForRow(actualRows[actualRows.length - 1]) : 0;
                  const areaD = `${actualPath} L${lastX},${baseY} L${firstX},${baseY} Z`;
                  return (
                    <path
                      key={`area-${projectGUID}-${viewMinMax.min}-${viewMinMax.max}`}
                      d={areaD}
                      className={styles.scheduleChartAreaActual}
                    />
                  );
                })() : null}

                {/* 3. Actual line draws on — or variance segments */}
                {showVarianceColoring && varianceSegments.length > 0 ? (
                  <g>
                    {varianceSegments.map((seg, idx) => (
                      <line
                        key={`varseg-${idx}`}
                        x1={seg.x1}
                        y1={seg.y1}
                        x2={seg.x2}
                        y2={seg.y2}
                        stroke={seg.color}
                        strokeWidth={2.6}
                        strokeLinecap="round"
                      />
                    ))}
                  </g>
                ) : actualPath ? (
                  <path
                    key={`actual-${projectGUID}-${viewMinMax.min}-${viewMinMax.max}`}
                    d={actualPath}
                    className={styles.scheduleChartLineActual}
                    pathLength={1}
                  />
                ) : null}

                {/* 4. Dots pop in with stagger */}
                {actualRows.map((r, idx) => {
                  const raw = Number(r.actual_spend);
                  if (!Number.isFinite(raw)) return null;
                  const cx = xForRow(r);
                  const cy = yForValue01(clamp01(raw / effectiveScaleFactor));
                  const delay = 1200 + idx * 120;
                  return (
                    <circle
                      key={`actual-dot-${idx}`}
                      cx={cx}
                      cy={cy}
                      r={3}
                      className={styles.scheduleChartDot}
                      style={{ animationDelay: `${delay}ms` }}
                    />
                  );
                })}

                {/* 5. Forecast band + line enter after dots finish */}
                {forecastBandPath ? (() => {
                  const dotFinish = 1200 + actualRows.filter(r => Number.isFinite(Number(r.actual_spend))).length * 120 + 280;
                  return (
                    <path
                      d={forecastBandPath}
                      className={styles.scheduleChartBandForecast}
                      style={{ animationDelay: `${dotFinish}ms` }}
                    />
                  );
                })() : null}

                {forecastPath ? (() => {
                  const dotFinish = 1200 + actualRows.filter(r => Number.isFinite(Number(r.actual_spend))).length * 120 + 280;
                  return (
                    <path
                      key={`forecast-${projectGUID}-${viewMinMax.min}-${viewMinMax.max}`}
                      d={forecastPath}
                      className={styles.scheduleChartLineForecast}
                      pathLength={1}
                      style={{ animationDelay: `${dotFinish + 100}ms` }}
                    />
                  );
                })() : null}

                {/* Hover vertical + markers + dynamic threshold lines */}
                {hoverRow
                  ? (() => {
                      const hx = xForRow(hoverRow);

                      const hoverBaselineRaw = Number(hoverRow.baseline_budget);
                      const hoverBaselinePct100 = Number.isFinite(
                        hoverBaselineRaw,
                      )
                        ? effectiveScaleFactor > 0
                          ? Math.max(0, (hoverBaselineRaw / effectiveScaleFactor) * 100)
                          : 0
                        : null;

                      // Dynamic warning/critical lines at hovered baseline (cost overrun thresholds ABOVE baseline)
                      const hoverWarnPct100 =
                        hoverBaselinePct100 !== null
                          ? Math.max(0, hoverBaselinePct100 + tolYellowPP)
                          : null;
                      const hoverCritPct100 =
                        hoverBaselinePct100 !== null
                          ? Math.max(0, hoverBaselinePct100 + tolRedPP)
                          : null;

                      const by =
                        hoverRow.baseline_budget !== null &&
                        hoverRow.baseline_budget !== undefined
                          ? yForValue01(
                              clamp01(
                                Number(hoverRow.baseline_budget) / effectiveScaleFactor,
                              ),
                            )
                          : null;
                      const ay =
                        hoverRow.actual_spend !== null &&
                        hoverRow.actual_spend !== undefined
                          ? yForValue01(
                              clamp01(
                                Number(hoverRow.actual_spend) / effectiveScaleFactor,
                              ),
                            )
                          : null;
                      const fy =
                        hoverRow.forecast_spend !== null &&
                        hoverRow.forecast_spend !== undefined
                          ? yForValue01(
                              clamp01(
                                Number(hoverRow.forecast_spend) / effectiveScaleFactor,
                              ),
                            )
                          : null;

                      return (
                        <>
                          {/* Dynamic warning threshold at hovered baseline */}
                          {hoverWarnPct100 !== null && (
                            <line
                              x1={padL}
                              y1={yForValue01(hoverWarnPct100 / 100)}
                              x2={W - padR}
                              y2={yForValue01(hoverWarnPct100 / 100)}
                              stroke="rgba(245, 158, 11, 0.5)"
                              strokeWidth={1.5}
                              strokeDasharray="6,4"
                            />
                          )}
                          {/* Dynamic critical threshold at hovered baseline */}
                          {hoverCritPct100 !== null && (
                            <line
                              x1={padL}
                              y1={yForValue01(hoverCritPct100 / 100)}
                              x2={W - padR}
                              y2={yForValue01(hoverCritPct100 / 100)}
                              stroke="rgba(239, 68, 68, 0.5)"
                              strokeWidth={1.5}
                              strokeDasharray="6,4"
                            />
                          )}

                          <line
                            x1={hx}
                            y1={padT}
                            x2={hx}
                            y2={H - padB}
                            className={styles.scheduleChartHoverLine}
                          />
                          {by !== null ? (
                            <circle
                              cx={hx}
                              cy={by}
                              r={3.5}
                              className={styles.scheduleChartDotBaseline}
                            />
                          ) : null}
                          {ay !== null ? (
                            <circle
                              cx={hx}
                              cy={ay}
                              r={3.5}
                              className={styles.scheduleChartDot}
                            />
                          ) : null}
                          {fy !== null ? (
                            <circle
                              cx={hx}
                              cy={fy}
                              r={3.5}
                              className={styles.scheduleChartDotForecast}
                            />
                          ) : null}
                        </>
                      );
                    })()
                  : null}
              </svg>

              {/* Tooltip */}
              {hoverRow && hoverClient ? (
                <ThermometerTooltip
                  date={hoverRow.reporting_date}
                  rows={[
                    { label: t('thermometer.budget'), value: hoverRow.baseline_budget, format: fmtValue },
                    { label: t('thermometer.actualCost'), value: hoverRow.actual_spend, format: fmtValue },
                    { label: t('thermometer.forecast'), value: hoverRow.forecast_spend, format: fmtValue },
                  ]}
                  x={hoverClient.x}
                  y={hoverClient.y}
                />
              ) : null}
            </>

          {/* close scheduleChartPanel */}
        </div>
      </div>

      {/* Legend — rendered outside chart body so it doesn't require scrolling */}
      {showLegend ? (
        <ThermometerLegend
          items={[
            { label: t('thermometer.baseline'), className: styles.legendBaseline },
            { label: t('thermometer.actual'), className: styles.legendActual },
            { label: t('thermometer.forecast'), className: styles.legendForecast },
            { label: t('thermometer.forecastBand'), className: styles.legendForecastBand },
            { label: t('thermometer.today'), className: styles.legendToday },
          ]}
          hint="Wheel to zoom • Drag to pan • Double‑click to reset"
        />
      ) : null}

      {/* ── Pipeline Status Log (debug mode only) ── */}
      {debugMode && statusLog.length > 0 ? (
        <PipelineStatusLog
          statusLog={statusLog}
          statusLogRef={statusLogRef}
          onClear={() => setStatusLog([])}
        />
      ) : null}
    </>
  );

  // When embedded inside a parent container (combined Progress + CPI box),
  // skip the outer scheduleChartWrap — the parent already provides it.
  const fullContent = embedded ? (
    fullContentInner
  ) : (
    <div className={styles.scheduleChartWrap}>{fullContentInner}</div>
  );

  /*
   * DEV-846: Render dispatch — see ScheduleThermometer for the full rationale.
   * Three shapes: non-mini → fullContent; mini collapsed → miniHeader only;
   * mini expanded → miniHeader + chart inline. No modal.
   */
  if (wantsMini) {
    return (
      // DEV-846: marginBottom mirrors ScheduleThermometer — see there for rationale.
      <div style={{ marginBottom: 14 }}>
        {miniHeader}
        {fullOpen ? (
          <div
            style={{
              border: "1px solid var(--border-default)",
              borderTop: "none",
              borderRadius: "0 0 12px 12px",
              padding: 12,
              background: "var(--card-bg)",
            }}
          >
            {fullContent}
          </div>
        ) : null}
      </div>
    );
  }

  return fullContent;
}
